// =============================================================================
// appShared.js — Constantes & helpers partagés extraits d'App.jsx
// (Révision S1 étape 0). Déplacement pur : chaque bloc est repris à
// l'identique depuis App.jsx, seul le préfixe `export` a été ajouté.
// Contenu : palette C, helpers votants/listes (isParticipantVotant,
// resolveListMembers, resolveListEmails), référentiels statutaires
// (POSTES_BUREAU, POLES_PAR_DOMAINE, EXPERTISES_DEFAUT, INSTANCE_TYPES,
// STATUTS, STATUTS_ACTIFS, REGIMES_VOTE) et petits badges d'UI
// (VoteBadge, ResultsBadge, InstanceBadge).
// =============================================================================
import { REGION_FRANCFORT } from "./regionsFonctions";
import { Jauge } from "./Jauge";
import { SqueletteFichiers } from "./squelettes";
import { Suspense, useState, useEffect, useRef } from "react";
import { lazyRetry } from "./lazyRetry";
import { delegateIsQuorumMember } from "./quorumHelper";
import { sanitizeHtml as sanitizeAmdtHtml, isHtmlContent as isAmdtHtml } from "./amendementsCommun";
import { ITEM_STATUT_META, normalizeItem } from "./projetShared";
import { MUTUELLE_ALIAS, normalizeMutuelleName } from "./mutuellesHelper";
import { useConfirm, ModalShell } from "./uxComponents";
import { POSTES_BUREAU } from "./postesBureau";
import { membresBureau, membresDeListe, listesDiffusion, listesAutomatiques } from "./listesDiffusion";
import { zoomCssDisponible, poserEchelleTexte, poserAjustementTexte, taillePx } from "./echelleTexte";
import { modaliteVote, compterCandidats } from "./modaliteVote";
import { INSTANCES_NAV, estDeliberationInformelle, typeSeance } from "./instancesNav";
import { C } from "./couleurs";
import { navTexte, NAV_COLORS } from "./navConstants";
import { Icone, IconeTexte } from "./icones";
import { estVideo } from "./videoDocuments";
import { BIBLIOTHEQUES, chargerJsPDF } from "./bibliothequesServies";

// Sprint 13 v3 : helper pour déterminer si un participant Thémis est VOTANT
// (membre du quorum) ou OBSERVATEUR (invité hors quorum).
//   - CA → seuls les délégués avec rôle CA et mandat CA actif sont votants
//   - AG → seuls les délégués avec rôle AG et mandat AG actif sont votants
//   - Bureau → seuls les membres avec poste actif sont votants
// Match délégué : par email (lowercase + trim), avec fallback prénom+nom.
// Tout participant non matché = observateur (CODIR, invité externe, etc.).
//
// /!\ Pour les types d'instance qui ne sont PAS CA/AG/Bureau (CODIR, VL,
// libre…), TOUT LE MONDE est votant (le quorum statutaire ne s'applique pas).
// ── Le Bureau est une instance INFORMELLE (décision Pierre, 27/08/2026) ──────
// « Le Bureau ne vote pas, pas de quorum, rien. C'est une instance informelle. »
// Il continue d'exister pleinement par ailleurs : convocations, dossier
// SharePoint, postes de l'Art. 31, mandature, historique. Ce qu'il n'a pas,
// c'est une délibération — donc ni voix, ni quorum, ni PV chiffré.
//
// Le type est reconnu sous toutes ses écritures : « BU » (code canonique écrit
// par le formulaire de séance) et « Bureau » (libellé legacy des séances et
// réunions jamais repassées par ce formulaire). La règle vit dans
// src/instancesNav.js (`deliberation`), avec celle du CODIR, qui délibère sans
// quorum ni pouvoirs (estSansQuorum, estSansPouvoirs).
export const estInstanceInformelle = (t) => estDeliberationInformelle(t);

export function isParticipantVotant(participant, instanceType, delegates, gouvernance) {
  if (!participant) return false;
  // Instance informelle : PERSONNE ne vote — pas même un administrateur en
  // exercice. Cette garde passe AVANT la branche « hors quorum statutaire »
  // ci-dessous, qui rendait au contraire tout le monde votant sur « BU ».
  if (estInstanceInformelle(instanceType)) return false;
  // OBSERVATEUR EXPLICITE (26/08/2026) — invité extérieur convié à assister
  // sans prendre part aux délibérations (contact du carnet Événements convié
  // à l'AG). Ce drapeau prime sur TOUT, y compris sur la branche « instance
  // hors quorum statutaire » ci-dessous : sur une séance VL/SE/BU/CD, un
  // externe serait sinon compté votant (il ne l'est jamais).
  if (participant.observateur === true) return false;
  // Pour les instances non-soumises au quorum statutaire, on garde le
  // comportement historique : tout participant actif est votant. Type
  // canonique (src/instancesNav.js) : « ca » ou « Conseil d'administration »
  // restent un CA.
  if (!["CA", "AG"].includes(typeSeance(instanceType))) return true;
  const email = (participant.email || "").toLowerCase().trim();
  let delegate = null;
  if (email) {
    delegate = (delegates || []).find(d => (d.email || "").toLowerCase().trim() === email);
  }
  if (!delegate) {
    // Fallback : match par prénom + nom normalisés (utile quand l'admin a
    // ajouté quelqu'un sans email, ou que les casses diffèrent)
    const np = (participant.prenom || "").toLowerCase().trim();
    const nn = (participant.nom || "").toLowerCase().trim();
    if (np && nn) {
      delegate = (delegates || []).find(d =>
        (d.prenom || "").toLowerCase().trim() === np
        && (d.nom || "").toLowerCase().trim() === nn);
    }
  }
  if (!delegate) return false;  // pas trouvé dans delegates → observateur
  return delegateIsQuorumMember(delegate, typeSeance(instanceType), gouvernance);
}

// Palette d'accents : dans src/couleurs.js depuis le lot L10 (module sans
// dépendance : uxComponents.js, que ce fichier importe, peut la lire sans
// cycle). Réexportée ici, les importeurs ne changent pas.
export { C };

// Jetons de mouvement (MIROIR de la section « Mouvement » de theme.css) et
// mouvement réduit partagé, pour les styles en ligne et WAAPI — lot L2. Ils
// vivent dans src/mouvement.js (sans dépendance, que le Vote AG importe sans
// tirer ce fichier) ; on les réexporte ici, à côté de C.
export { MOUVEMENT, courbeRessort, mouvementReduit, useMouvementReduit } from "./mouvement";
// Une couleur à une opacité : alpha(C.teal, .13) → rgba() ;
// alpha("var(--acc-teal)", .08) → color-mix(). Remplacera C.teal+"22" au lot L10.
export { alpha } from "./alpha";

// Résout les DÉLÉGUÉS (objets annuaire) d'une liste memberLists : les fiches
// de la liste (mutuelle du même nom, membres explicites rapprochés par
// identifiant, adresse, puis prénom, nom et mutuelle), puis les membres sans
// fiche qui ont une adresse (pseudo-identités « ext_<adresse> »). La règle vit
// dans src/listesDiffusion.js (membresDeListe), miroir du serveur.
export function resolveListMembers(l, delegates = []) {
  if (!l) return [];
  const r = membresDeListe(l, delegates);
  return [...r.membres, ...r.horsFicheMembres];
}
export function resolveListEmails(l, delegates = []) {
  return resolveListMembers(l, delegates).filter(d => d.email).map(d => d.email);
}

// Postes du bureau Enio (Art. 31 des statuts)
/**
 * Le contingent statutaire d'une délégation est-il atteint ?
 * (2 administrateurs par mutuelle au CA, 5 délégués à l'AG — INSTANCE_TYPES.)
 *
 * Il ne compte QUE les VOTANTS : une mutuelle peut faire venir autant
 * d'invités qu'elle veut — directeurs, collaborateurs, membres du Comité de
 * direction, observateurs —, ils ne consomment aucun siège. Compter tout le
 * monde interdisait de remettre un délégué « présent » dès qu'une mutuelle
 * avait plus de cinq personnes dans la salle (bug signalé par Pierre le
 * 08/09/2026).
 *
 * @param estVotant  prédicat (isParticipantVotant lié à l'instance)
 * @param exclureId  la ligne qu'on est en train de modifier
 */
export function contingentDelegationAtteint(participants = [], {
  mutuelle, max, estVotant, exclureId = null, delegationDe = null,
} = {}) {
  // Toujours la même forme de retour : { atteint, pris, delegation }.
  if (max === null || max === undefined) return { atteint: false, pris: 0, delegation: mutuelle };
  // Par DÉLÉGATION, pas par libellé de ligne : les administrateurs portés par
  // une faîtière (UMGP) siègent pour MEP ou pour SMEREP, chacune avec son
  // contingent. Sans résolveur, on retombe sur la mutuelle de la ligne.
  const cle = (p) => (delegationDe ? delegationDe(p) : (p.mutuelle || ""));
  const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  const cible = norm(mutuelle);
  const pris = participants.filter((p) => p && p.id !== exclureId
    && norm(cle(p)) === cible && isActif(p) && estVotant(p));
  return { atteint: pris.length >= max, pris: pris.length, delegation: mutuelle };
}

// ── Point d'ordre du jour ⇄ point interstitiel (demande Pierre 03/09/2026) ──
// Un interstitiel (pause, propos liminaires, déjeuner) tient le déroulé minuté
// mais ne figure NI à l'ordre du jour de la convocation, NI dans la séance, NI
// au procès-verbal : tous les consommateurs filtrent isInterstitiel.
// La bascule NEUTRALISE le scrutin sans purger les champs de fond (délibération,
// intervenants, conflits, pièces) : l'aller-retour ne perd rien.

/** Point → pause : plus de scrutin, plus de hiérarchie. `typeVote` n'est PAS
 *  écrasé — sans quoi l'aller-retour perdrait un scrutin secret (il est sans
 *  effet ici : tous les consommateurs filtrent isInterstitiel, et scrutin=false). */
export const enInterstitiel = (o) => ({ ...o, isInterstitiel: true, scrutin: false, niveau: 0 });

/** Pause → point : scrutin rétabli avec des valeurs valides, tirets décoratifs retirés. */
export const enPointOrdinaire = (o) => ({
  ...o, isInterstitiel: false, scrutin: true,
  typeVote: (o.typeVote && o.typeVote !== "none") ? o.typeVote : "public",
  scrutinType: o.scrutinType || "yon",
  regimeVote: o.regimeVote || "ordinaire",
  titre: String(o.titre || "").replace(/^\s*[—–-]\s*/, "").replace(/\s*[—–-]\s*$/, "").trim(),
});

/**
 * Le scrutin qui EMPÊCHE de transformer un point en pause : la réconciliation
 * de genererScrutins ne retire que les scrutins en attente et sans vote ; un
 * scrutin voté, ouvert ou clos resterait dans la séance et au PV, orphelin
 * d'un point devenu invisible. Renvoie le scrutin bloquant, ou null.
 */
export function scrutinBloquantBascule(point, agenda = [], votesByScrutin = {}) {
  if (!point) return null;
  const lies = (agenda || []).filter((a) => a && (a.odjPointId === point.id
    || (!a.odjPointId && a.question === point.titre)));
  return lies.find((a) => (votesByScrutin[a.id] || []).length > 0
    || (a.status && a.status !== "pending")) || null;
}

// Source : src/postesBureau.js (module léger, lu aussi par effetsStatutaires.js).
export { POSTES_BUREAU };

export const POLES_PAR_DOMAINE = {
  "DÉVELOPPEMENT & FIDÉLISATION": [
    "Offres, distribution et marketing",
    "Gestion & Relation adhérents",
    "Cellule de réponse aux Appels d'offres",
  ],
  "AFFAIRES EXTÉRIEURES": [
    "Relations extérieures et partenariats",
    "Communication et presse",
    "Affaires publiques",
  ],
  "IMPACT": [
    "RSE (Responsabilité Sociale de l'Entreprise)",
    "Prévention et juste soin",
    "Innovation & investissements stratégiques",
  ],
  "PILOTAGE ÉCONOMIQUE ET RÉGLEMENTAIRE": [
    "Actuariat et pilotage de la donnée",
    "Conformité, Juridique et Protection des données",
    "Gestion des risques, contrôle interne et audit interne",
    "Administration, Comptabilité & finance",
  ],
  "RESSOURCES": [
    "Ressources humaines",
    "Informatique, outils et sécurité",
    "Négociation & Acquisition (Achats)",
  ],
};

// ── Expertises professionnelles — liste fermée (secteur mutualité/assurance)
// Stockée dans config.expertises (Firestore). Valeur par défaut ci-dessous.
export const EXPERTISES_DEFAUT = {
  "Santé & Prévoyance": [
    "Actuariat santé","Tarification santé","Tarification prévoyance",
    "Gestion des prestations","Gestion déléguée","Tiers payant","RAC 0",
    "Complémentaire santé obligatoire (ANI)","Prévoyance collective",
    "Garanties décès/incapacité/invalidité","Épargne","Retraite",
  ],
  "Conformité & Juridique": [
    "Solvabilité II","ORSA","DDA","DORA","RGPD/DPO",
    "Gouvernance mutualiste","Droit des assurances","Droit des contrats",
    "Droit des sociétés","Création/Dissolution de sociétés",
    "Lutte anti-blanchiment (LCB-FT)",
  ],
  "Distribution & Marketing": [
    "Courtage","Distribution directe","Marketing digital",
    "CRM & fidélisation","Appels d'offres publics","Partenariats institutionnels",
  ],
  "Finance & Comptabilité": [
    "Comptabilité assurance","Normes IFRS 17","Reporting prudentiel",
    "Gestion d'actifs","Réassurance","Combinaison des comptes","Fiscalité",
  ],
  "IT & Data": [
    "Systèmes d'information santé","SI prévoyance","SI épargne",
    "Cybersécurité","Data science / IA","Dématérialisation",
    "Interopérabilité (SESAM-Vitale, ROC)",
  ],
  "RH & Gouvernance": [
    "Relations sociales","Formation professionnelle",
    "Élections mutualistes","Gouvernance d'UGM",
  ],
  "Communication & RSE": [
    "Communication institutionnelle","RSE & impact social",
    "Prévention santé","Relations presse",
  ],
};

export const INSTANCE_TYPES = {
  CA: { label:"Conseil d'administration", short:"CA", maxPerMutuelle:2, maxPouvoirs:0 },
  AG: { label:"Assemblée Générale",       short:"AG", maxPerMutuelle:5, maxPouvoirs:2 },
  CD: { label:"Comité de direction",      short:"CODIR", maxPerMutuelle:null, maxPouvoirs:0 },
  BU: { label:"Bureau",                   short:"BU", maxPerMutuelle:null, maxPouvoirs:0 },
  CO: { label:"Comité",                   short:"CO", maxPerMutuelle:null, maxPouvoirs:0 },
  SE: { label:"Séminaire",               short:"SE", maxPerMutuelle:null, maxPouvoirs:0 },
  VL: { label:"Vote Libre",               short:"VL", maxPerMutuelle:null, maxPouvoirs:0 },
  CS: { label:"Consultation asynchrone",  short:"CS", maxPerMutuelle:null, maxPouvoirs:0, async:true },
};

// Alias historiques de type d'instance rencontrés en base : le formulaire
// stocke les codes (CD, BU) mais d'anciennes sessions portent les libellés
// (CODIR, Bureau) — les couleurs et dossiers SharePoint divergeaient selon
// l'écriture. Normaliser AVANT toute comparaison. La table d'alias est celle
// de src/instancesNav.js (typeSeance), jamais recopiée ici.
export const normalizeInstanceType = (t) => typeSeance(t);

// Couleur d'accent par type d'instance. Les quatre instances statutaires
// prennent leur sous-teinte dans src/instancesNav.js (source unique, lot L3) :
// AG bleu, CA teal, CODIR violet, Bureau orange — la préparation des séances
// (Pilotage) montrait jusqu'ici CA violet, AG orange et CODIR teal.
const COULEUR_INSTANCE = Object.fromEntries(INSTANCES_NAV.map((i) => [i.sessionType, i.couleur]));
export const INSTANCE_TYPE_COLORS = {
  CA: COULEUR_INSTANCE.CA, AG: COULEUR_INSTANCE.AG, CD: COULEUR_INSTANCE.CD, BU: COULEUR_INSTANCE.BU, CO: "#1D948A",
  SE: "#003F59", VL: "#003F59", CS: "#003F59",
};
export const instanceTypeColor = (t) => INSTANCE_TYPE_COLORS[normalizeInstanceType(t)] || "#003F59";

// « Sans reponse » (demande Pierre 27/08) : statut PAR DÉFAUT d'un invité qui
// n'a pas encore répondu. Il est explicite — à la différence de l'ancien champ
// vide, qui s'affichait « statut non renseigné » sans qu'on sache s'il
// s'agissait d'un oubli ou d'une absence de réponse. Il n'est PAS actif : un
// invité sans réponse ne compte ni parmi les présents, ni dans le quorum.
export const STATUT_DEFAUT = "Sans reponse";
export const STATUTS = ["Sans reponse","Present","Present en visioconference","Excuse","Absent"];
export const STATUTS_ACTIFS = ["Present","Present en visioconference"];
// Un participant enregistré AVANT l'introduction du statut par défaut porte un
// champ vide : on l'affiche « Sans reponse » plutôt que de le laisser hors des
// listes déroulantes (l'ancien « statut non renseigné »).
export const statutAffiche = (p) => (p && p.statut) || STATUT_DEFAUT;

// Règles statutaires par régime de vote
export const REGIMES_VOTE = {
  ordinaire: {
    label: "Ordinaire",
    quorumFraction: 1/4,        // Art. 16 : quart des délégués
    majorite: "simple",         // majorité simple des voix exprimées
    description: "Quorum 1/4 — Majorite simple"
  },
  renforce: {
    label: "Renforce",
    quorumFraction: 1/2,        // Art. 16 al.1 : moitié des délégués
    majorite: "deux_tiers",     // 2/3 des voix
    description: "Quorum 1/2 — Majorite 2/3 (statuts, fusion, dissolution)"
  },
};

export const VoteBadge    = ({secret})    => <span className={secret?"badge-secret":"badge-public"}>{secret?"Bulletin secret":"Vote public"}</span>;
export const ResultsBadge = ({published}) => <span className={published?"badge-published":"badge-hidden"}>{published?"Résultats publiés":"Résultats masqués"}</span>;
export const InstanceBadge = ({type,label}) => <span className={`badge-${type}`}>{label||INSTANCE_TYPES[type]?.label||type}</span>;

// ── Palette de couleurs par mutuelle (annuaire, organigramme) ──────────────
// Extrait d'App.jsx (Révision S1-1 bonus) — déplacement pur, préfixe export.
export const MUT_COLORS = [
  "#003F59","#1D948A","#F49800","#1a5a7a","#6d4c41",
  "#4a7c59","#7b5ea7","#b5451b","#2e6da4","#5c6bc0",
  "#00796b","#E20816","#1565c0","#558b2f","#6a1550",
];
export const getMutColor = (mut, mutuelles) => {
  const idx = mutuelles.indexOf(mut);
  return idx>=0 ? MUT_COLORS[idx % MUT_COLORS.length] : "#888";
};

// ── Domaines & pôles de l'organisation Enio (organigramme, accès par domaine) ──
// Extrait d'App.jsx (Révision S1-1 bonus) — déplacement pur, préfixe export.
// `icone` + `description` : présentation pédagogique du module Domaines &
// Pôles — un dirigeant de mutuelle candidate ou un nouvel arrivant doit
// comprendre chaque domaine sans connaître l'organisation interne d'Enio.
export const ORG_DOMAINES = [
  {id:"dev",  label:"Développement & Fidélisation", folderName:"Développement et Fidélisation", couleur:"#1D948A", icone:"🚀",
    description:"Faire grandir les mutuelles membres : conquête et fidélisation des adhérents, offres et distribution, marketing, et réponses groupées aux appels d'offres.",
    poles:["Offres, distribution et marketing","Gestion & Relation adhérents","Cellule de réponse aux Appels d'offres"]},
  {id:"aff",  label:"Affaires extérieures",          folderName:"Affaires Extérieures", couleur:"#185fa5", icone:"🤝",
    description:"Porter la voix d'Enio et de ses mutuelles à l'extérieur : partenariats, relations institutionnelles, communication, presse et affaires publiques.",
    poles:["Relations extérieures et partenariats","Communication et presse","Affaires publiques"]},
  {id:"imp",  label:"Impact",                        folderName:"Impact", couleur:"#3b6d11", icone:"🌱",
    description:"Donner corps aux engagements mutualistes : responsabilité sociale, prévention et juste soin, innovation et investissements stratégiques.",
    poles:["RSE (Responsabilité Sociale de l'Entreprise)","Prévention et juste soin","Innovation & investissements stratégiques"]},
  {id:"pil",  label:"Pilotage éco. & réglementaire", folderName:"Pilotage Économique et Réglementaire", couleur:"#534ab7", icone:"📐",
    description:"Sécuriser et piloter le collectif : actuariat et données, conformité et juridique, gestion des risques et audit, comptabilité et finance.",
    poles:["Actuariat et pilotage de la donnée","Conformité, Juridique et Protection des données","Gestion des risques, contrôle interne et audit interne","Administration, Comptabilité & finance"]},
  {id:"res",  label:"Ressources",                    folderName:"Ressources", couleur:"#993c1d", icone:"🧰",
    description:"Outiller le collectif : ressources humaines, informatique et sécurité des outils, achats et négociations groupées.",
    poles:["Ressources humaines","Informatique, outils et sécurité","Négociation & Acquisition (Achats)"]},
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers partagés Header + App (Révision S1 tranche 3) : session admin
// legacy (localStorage), taille de police (accessibilité) et thème jour/nuit.
// Déplacement pur depuis App.jsx, seuls les préfixes `export` ont été ajoutés.
// ─────────────────────────────────────────────────────────────────────────────

// ── Session admin legacy (localStorage) — ÉTEINTE le 28/09/2026 ────────────
// Ces deux prédicats acceptaient toute valeur BIEN FORMÉE de
// localStorage.eniovote_admin ({ id, prenom, ts, isSuperAdmin? }, moins de
// 30 jours) : un visiteur l'écrivait depuis la console du navigateur et
// l'écran le tenait pour administrateur — pour super-administrateur avec
// `isSuperAdmin:true` (menu Super-admin du header et de l'ex « Tout Aramis »,
// panneau de super-administration). La valeur n'était produite que par les
// portes supprimées de la console (formulaire `admins`, console mot de passe
// + TOTP super-admin, accès libre du premier démarrage) : audit du 28/09/2026.
//
// Ils renvoient désormais TOUJOURS false. Chaque appel reste écrit
// `aramisSession?.role === "admin" || … || hasValidLegacyAdmin()` : la
// décision repose donc sur le seul rôle de session, émis par le serveur au
// login (withRoleCanonique). Garde : src/superAdminLegacy.test.js.
export const hasValidLegacyAdmin = () => false;
export const hasValidLegacySuperAdmin = () => false;

// -- Taille de police (accessibilite) -----------------------------------------
export const getFontSize = () => { try{return localStorage.getItem("eniovote_fontsize")||"normal";}catch{return "normal";} };
export const setFontSizeLS = (s) => { try{localStorage.setItem("eniovote_fontsize",s);}catch{} };
export const FONT_SCALE_MAP = { normal:1, lg:1.18, xl:1.38 };

export const applyFontSize = (s) => {
  const scale = FONT_SCALE_MAP[s] || 1;
  // zoom scale tout le contenu y compris px hardcodés
  // --fs-scale N'EST PLUS POSÉ ICI : le `zoom` appliqué plus bas met déjà la
  // racine à l'échelle, si bien que les deux se multipliaient — « Grand »
  // agrandissait de 1,18² = 1,39 et « Très grand » de 1,38² = 1,90, au lieu des
  // valeurs annoncées. Le repli :root{--fs-scale:1} d'App.jsx reste en place, et
  // son unique consommateur (html{font-size:calc(13px * var(--fs-scale))})
  // continue donc de calculer sur 13 px, que `zoom` met ensuite à l'échelle une
  // seule fois. --fs-zoom, lui, est posé plus bas : c'est l'échelle que le zoom
  // applique RÉELLEMENT à la géométrie (src/echelleTexte.js).
  // Le réglage devient LISIBLE EN CSS (body.fs-normal / fs-lg / fs-xl). Motif :
  // le `zoom` ci-dessous rétrécit la mise en page (393 px d'iPhone deviennent
  // 333 px en « Grand », 285 px en « Très grand ») alors que les media queries
  // continuent de mesurer 393 px — les paliers « petit mobile » ne se
  // déclenchent donc jamais pour ceux qui ont justement besoin de gros
  // caractères. La feuille globale (src/feuilleGlobale.js, raffinements) rejoue
  // le palier manquant grâce à cette classe.
  // (On ne retire que les classes fs-*, comme applyTheme ne retire que theme-*.)
  document.body.className = (document.body.className.replace(/\bfs-[\w-]+\b/g, "")
    .replace(/\s+/g, " ").trim() + " fs-" + (FONT_SCALE_MAP[s] ? s : "normal")).trim();
  document.body.style.zoom = scale;
  let echelleRendue = zoomCssDisponible() ? scale : 1;
  // Fallback pour Firefox (pas de zoom CSS)
  if(typeof document.body.style.zoom === "undefined" || document.body.style.zoom === "") {
    document.body.style.transform = "scale("+scale+")";
    document.body.style.transformOrigin = "top left";
    document.body.style.width = (100/scale)+"%";
    echelleRendue = scale;
  }
  // Sous le zoom, vh/dvh/vw et les déplacements du doigt sont multipliés :
  // --fs-zoom (et echelleTexte()) permettent de les ramener à l'écran. Posée
  // dans le même geste que le zoom, donc sans saut au changement de taille.
  poserEchelleTexte(echelleRendue);
  // Safari iOS et iPadOS n'appliquent pas ce zoom aux polices déclarées : une
  // sonde le constate et pose data-texte-ajuste, qui rend l'échelle au texte
  // (« pourcentage » sur iPhone, « jetons » sur iPad, src/echelleTexte.js).
  poserAjustementTexte(echelleRendue);
};

// -- Thème jour / nuit -------------------------------------------------------
// Les fonctions vivent dans src/themeCoquille.js depuis le 15/10/2026 (elles
// tiraient uxComponents dans un cycle d'imports par preferencesCoquille, ce
// qui cassait le serveur de développement). Réexportées ici : les appelants
// historiques (App.jsx, index.js, AppPublique.js) n'ont pas bougé.
export { getTheme, themePagePublique, setThemeLS, applyTheme, themeEnVigueur,
  useThemePagePublique } from "./themeCoquille";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers & composants transverses (Révision S1 tranche 4) : consommés à la
// fois par App.jsx et par les composants extraits (Participant, EmailingView).
// Déplacement pur depuis App.jsx, seuls les préfixes `export` ont été ajoutés.
// ─────────────────────────────────────────────────────────────────────────────

// ── Micro-helpers génériques ────────────────────────────────────────────────
export const uid      = () => Math.random().toString(36).slice(2,9);
export const nowFull  = () => new Date().toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit",second:"2-digit"});

// Affichage tolérant d'un horodatage de session. Trois formats coexistent en
// base : timestamp ms (Date.now(), certaines réouvertures), ISO 8601 (toutes
// les écritures depuis themisLifecycle) et chaîne localisée fr-FR (nowFull,
// historique). Le PV imprimait la valeur brute — « Ouverte le : 1755264727123 ».
// Heure de fin EFFECTIVE d'une séance : celle du Cadre si saisie, sinon
// calculée depuis le déroulé (heure de début + somme des durées des points du
// brouillon d'ODJ, interstitiels compris — une pause consomme du temps).
// Retourne { heureFin: "HH:MM"|"", estimee: bool }.
export const heureFinEffective = (sess) => {
  if (sess?.heureFin) return { heureFin: sess.heureFin, estimee: false };
  const debut = sess?.heureDebut || "";
  const points = Array.isArray(sess?.odjDraft) ? sess.odjDraft : [];
  if (!/^\d{2}:\d{2}$/.test(debut) || points.length === 0) return { heureFin: "", estimee: false };
  const total = points.reduce((s2, p) => s2 + (parseInt(p.duree, 10) || 0), 0);
  if (total <= 0) return { heureFin: "", estimee: false };
  const [h, m] = debut.split(":").map(Number);
  const fin = h * 60 + m + total;
  return {
    heureFin: String(Math.floor(fin / 60) % 24).padStart(2, "0") + ":" + String(fin % 60).padStart(2, "0"),
    estimee: true,
  };
};

export const fmtDateHeure = (v) => {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "number" || /^\d{12,}$/.test(String(v))) {
    const d = new Date(Number(v));
    return Number.isNaN(d.getTime()) ? String(v)
      : d.toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"});
  }
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(v))) {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v)
      : d.toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"});
  }
  return String(v);   // chaîne fr-FR legacy : déjà lisible
};
export const isActif  = (p) => STATUTS_ACTIFS.includes(p.statut);

// ── Configuration d'instance (type AG/CA/VL/SE d'une session de vote) ──────
export const getInstConfig = (session) => {
  if (!session) return INSTANCE_TYPES.AG;
  // Type canonique : une séance enregistrée « CODIR » tombait sur la
  // configuration de l'AG (deux pouvoirs, cinq délégués par mutuelle).
  const type = typeSeance(session.instanceType);
  if (type !== "VL" && type !== "SE") {
    return INSTANCE_TYPES[type] || INSTANCE_TYPES.AG;
  }
  if (type === "SE") {
    return {
      label: "Seminaire",
      short: "SE",
      maxPerMutuelle: session.vlMaxPerOrg > 0 ? session.vlMaxPerOrg : null,
      maxPouvoirs: session.vlMaxPouvoirs || 0,
    };
  }
  return {
    label: "Vote Libre",
    short: "VL",
    maxPerMutuelle: session.vlMaxPerOrg > 0 ? session.vlMaxPerOrg : null,
    maxPouvoirs: session.vlMaxPouvoirs || 0,
  };
};


// -- Logo Enio ----------------------------------------------------------------
// Pictogramme VECTORIEL officiel (public/enio-picto.svg, export Illustrator) :
// net à toute taille/densité et servi hors bundle. Remplace l'ancien PNG en
// base64 inline (~56 Ko de JS). Le base64 ENIO_LOGO_B64 reste utilisé là où un
// raster est indispensable (jsPDF addImage, exports).
export const EnioLogo = ({size=32}) => (
  <img src="/enio-picto.svg" alt="Enio" style={{width:size,height:size,objectFit:"contain",flexShrink:0}}/>
);

// « Afficher » une pièce de la bibliothèque (04/10/2026) : visionneuse chargée
// à la demande, hors bundle principal (src/ApercuDocument.js).
const ApercuDocumentRaw = lazyRetry(() => import("./ApercuDocument"));
// Lecteur des vidéos (20/10/2026, src/LecteurVideoDocument.js), à la demande.
const LecteurVideoDocumentRaw = lazyRetry(() => import("./LecteurVideoDocument"));
const AFFICHABLE_RX = /\.(pdf|png|jpe?g|gif|webp|svg|docx?|xlsx?|pptx?|odt|ods|odp)$/i;

// Sélecteur réutilisable de documents SharePoint pour les emails
const SharePointDocPickerRaw = lazyRetry(() => import("./SharePointDocPicker"));
export const SharePointDocPicker = (props) => (<Suspense fallback={<span style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)"}}>…</span>}><SharePointDocPickerRaw {...props}/></Suspense>);

// ENIO_LOGO_B64 a déménagé dans src/enioLogoB64.js (perf P2) : consommé
// uniquement par les exports PDF/DOCX des chunks lazy — la garder ici la
// figeait dans main.js (~57 Ko gz). Importer depuis "./enioLogoB64".

// ─────────────────────────────────────────────────────────────────────────────
// Helpers partagés App ↔ AramisLogin ↔ MonProfilView (Révision S1 tranches
// 7-8) : cluster WebAuthn/passkey, normaliserInviteStatut, getExpertisesList,
// creerNotification. Déplacement pur depuis App.jsx, seuls les préfixes
// `export` ont été ajoutés (b64url / b64urlDecode restent internes).
// ─────────────────────────────────────────────────────────────────────────────

// ── WebAuthn / Passkey helpers ────────────────────────────────────────────────
const b64url = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)))
  .replace(/\+/g,"-").replace(/\//g,"_").replace(/=/g,"");

const b64urlDecode = (str) => {
  const s = str.replace(/-/g,"+").replace(/_/g,"/");
  return Uint8Array.from(atob(s), c=>c.charCodeAt(0)).buffer;
};

export const webAuthnSupported = () =>
  typeof window !== "undefined" &&
  !!window.PublicKeyCredential &&
  !!navigator.credentials;

// ── ENRÔLEMENT d'une passkey, VÉRIFIÉ CÔTÉ SERVEUR (16/09/2026) ────────────
// `registerPasskey()` a été supprimée : elle rendait la clé publique au
// navigateur, qui l'écrivait sur la fiche délégué (`delegates/`, modifiable
// par n'importe qui). Or le login lisait cette clé : y déposer la sienne
// ouvrait le compte d'autrui, sans mot de passe ni code. La cérémonie complète
// passe désormais par le serveur — défi émis par `passkeyChallenge`,
// attestation vérifiée et clé rangée dans `credentials/` par `passkeyEnroler`.
//
// 28/09/2026 — deuxième passkey de secours, indépendante d'iCloud. Deux types :
//   • « appareil » (défaut, inchangé) : Touch ID / Face ID / Windows Hello,
//     authentificateur de PLATEFORME ;
//   • « cle-securite » : YubiKey ou autre clé FIDO2 (USB, NFC) —
//     authentificateur ITINÉRANT, en clé
//     RÉSIDENTE obligatoire : la connexion (loginWithPasskey) ne donne aucun
//     identifiant au navigateur (allowCredentials vide), elle ne retrouve donc
//     que les passkeys que l'authentificateur sait présenter seul, et le
//     serveur reconnaît le compte au userHandle (= delegate.id) qu'elles
//     portent. Une clé non résidente serait enregistrée… et introuvable.
// La biométrie ou le code PIN de la clé est exigé dans les deux cas
// (userVerification « required » : le serveur refuse une clé sans le drapeau UV).
// `exclure` (identifiants rendus par passkeyLister) : sans lui, créer une
// passkey sur un authentificateur qui en porte déjà une pour ce compte
// l'ÉCRASE (même rp, même user.id) au lieu d'en ajouter une seconde. Depuis la
// relecture du 28/09/2026, le serveur rend aussi cette liste avec le défi :
// les deux sont réunies, si bien que TOUT appelant (écran de connexion, Ma
// semaine) en profite sans rien passer.
// `totpCode` : la preuve forte exigée par le serveur quand la session n'a pas
// été ouverte par une passkey. Elle part AVEC LA DEMANDE DE DÉFI : le serveur
// refuse avant la cérémonie, donc avant que l'authentificateur ne crée quoi
// que ce soit (un refus après coup laissait une clé orpheline, voire écrasait
// la passkey du trousseau). Elle est renvoyée à passkeyEnroler pour la seule
// fenêtre de déploiement (défi émis par l'ancienne version du serveur).

/** Options WebAuthn de création, selon le type (pure, testée). */
export const optionsCreationPasskey = ({ challenge, delegate, type = "appareil", exclure = [], rpId }) => {
  const cleSecurite = type === "cle-securite";
  return {
    challenge: b64urlDecode(challenge),
    rp: { name: "Aramis Enio", id: rpId },
    user: {
      id: new TextEncoder().encode(delegate.id),
      name: delegate.email || delegate.id,
      displayName: (delegate.prenom || "") + " " + (delegate.nom || ""),
    },
    // ES256 seul : c'est le format que le serveur sait vérifier (P-256).
    pubKeyCredParams: [{ type: "public-key", alg: -7 }],
    authenticatorSelection: cleSecurite
      ? {
        authenticatorAttachment: "cross-platform",
        userVerification: "required",
        residentKey: "required",
        requireResidentKey: true,   // navigateurs d'avant residentKey (WebAuthn niveau 1)
      }
      : {
        authenticatorAttachment: "platform",
        userVerification: "required",
        residentKey: "preferred",
      },
    // Indice WebAuthn niveau 3 : Chrome ouvre directement l'écran « clé de
    // sécurité » (USB/NFC) plutôt que le QR code d'un téléphone : c'est bien
    // une clé physique qu'on enregistre ici. Ignoré sans dommage par les
    // navigateurs qui ne le lisent pas.
    ...(cleSecurite ? { hints: ["security-key"] } : {}),
    excludeCredentials: (Array.isArray(exclure) ? exclure : [])
      .filter(Boolean)
      .map((id) => ({ type: "public-key", id: b64urlDecode(String(id)) })),
    // Le temps de sortir la clé, de la brancher et de saisir son code PIN.
    timeout: cleSecurite ? 120000 : 60000,
    attestation: "none",
  };
};

const libelleParDefaut = (type) => {
  if (type === "cle-securite") return "Clé de sécurité";
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (/iPhone|iPad/.test(ua)) return "iPhone";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Android/.test(ua)) return "Android";
  if (/Windows/.test(ua)) return "Windows";
  return "Navigateur";
};

export const enrolerPasskey = async (delegate, options = {}) => {
  // Compatibilité : l'ancien second argument était le libellé seul.
  const opts = typeof options === "string" ? { libelle: options } : (options || {});
  const type = opts.type === "cle-securite" ? "cle-securite" : "appareil";
  if (!webAuthnSupported()) throw new Error("WebAuthn non supporté sur cet appareil.");
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fn = getFunctions(undefined, REGION_FRANCFORT);
  const code = String(opts.totpCode || "").replace(/\s/g, "");
  // La preuve part avec la demande de défi : un refus tombe ICI, avant
  // navigator.credentials.create().
  const { data: def } = await httpsCallable(fn, "passkeyChallenge")({
    usage: "enrolement", ...(code ? { totpCode: code } : {}),
  });
  if (!def || !def.challenge) throw new Error("Défi indisponible — réessayez.");
  const exclure = [...new Set([
    ...(Array.isArray(def.exclure) ? def.exclure : []),
    ...(Array.isArray(opts.exclure) ? opts.exclure : []),
  ].filter(Boolean).map(String))];
  const cred = await navigator.credentials.create({
    publicKey: optionsCreationPasskey({
      challenge: def.challenge, delegate, type, exclure, rpId: window.location.hostname,
    }),
  });
  if (!cred) throw new Error("Enregistrement annulé.");
  const { data } = await httpsCallable(fn, "passkeyEnroler")({
    challenge: def.challenge,
    credential: {
      credentialId: b64url(cred.rawId),
      attestationObject: b64url(cred.response.attestationObject),
      clientDataJSON: b64url(cred.response.clientDataJSON),
      transports: cred.response.getTransports ? cred.response.getTransports() : [],
    },
    libelle: String(opts.libelle || "").trim().slice(0, 60) || libelleParDefaut(type),
    type,
    ...(code ? { totpCode: code } : {}),
  });
  return data || { ok: true };
};

/** Ses propres passkeys (libellé, date, type ; jamais la clé publique), et ce
 *  qu'il faudra présenter pour en ajouter ou en retirer. Appelée à
 *  l'ouverture de « Mon profil » : attend donc que la session Firebase soit
 *  prête (sinon l'appel part sans jeton et revient « unauthenticated »). */
export const listerPasskeys = async () => {
  const { attendreAuthPrete } = await import("./authGuard");
  await attendreAuthPrete();
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fn = getFunctions(undefined, REGION_FRANCFORT);
  const { data } = await httpsCallable(fn, "passkeyLister")({});
  return data || { ok: true, cles: [], exclure: [], preuve: "impossible" };
};

/** Retire une clé — côté serveur : l'effacer sur la fiche ne révoquait rien,
 *  la clé vivant dans credentials/. La cible est l'empreinte rendue par
 *  passkeyLister (`{ cle }`) ; une chaîne seule est lue comme l'identifiant
 *  complet (écrans d'avant le 28/09/2026). Depuis cette date, le serveur exige
 *  la même preuve forte que pour l'ajout, et refuse un appel SANS cible dès
 *  que le compte a plus d'une clé : tout retirer se demande par
 *  `{ toutes: true }`. */
export const retirerPasskey = async (cible, { totpCode } = {}) => {
  const c = typeof cible === "string" ? { credentialId: cible } : (cible || {});
  const donnees = {};
  if (c.cle) donnees.cle = String(c.cle);
  if (c.credentialId) donnees.credentialId = String(c.credentialId);
  if (c.toutes === true && !donnees.cle && !donnees.credentialId) donnees.toutes = true;
  const code = String(totpCode || "").replace(/\s/g, "");
  if (code) donnees.totpCode = code;
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fn = getFunctions(undefined, REGION_FRANCFORT);
  const { data } = await httpsCallable(fn, "passkeyRetirer")(donnees);
  return data || { ok: true };
};

// NOTE (2026-07-27) : `authenticatePasskey()` a été SUPPRIMÉE. Elle tirait le
// défi dans le navigateur et se contentait du succès de la cérémonie locale
// (« vérification côté client simplifiée ») : le facteur était décoratif —
// contourner le JavaScript suffisait à sauter la biométrie. Ses deux usages
// (step-up après mot de passe, attribution de rôle dans le panneau
// super-admin) passent désormais par loginWithPasskey() ci-dessous, dont le
// défi est émis ET l'assertion vérifiée côté serveur. Ne pas la réintroduire.

// ── Connexion par passkey, vérifiée CÔTÉ SERVEUR (2026-07-27) ───────────────
// Contrairement à authenticatePasskey() ci-dessus (héritage : défi tiré dans le
// navigateur, aucune vérification serveur → facteur décoratif), ce flux suit la
// vraie cérémonie WebAuthn : le SERVEUR émet le défi, puis vérifie signature,
// origine, rpId, présence et biométrie avant d'ouvrir la session.
// allowCredentials VIDE : on laisse l'appareil proposer les comptes qu'il
// connaît (passkeys « découvrables ») — l'utilisateur n'a donc NI e-mail NI
// mot de passe à saisir. Le userHandle renvoyé identifie le délégué.
export const loginWithPasskey = async () => {
  if (!webAuthnSupported()) throw new Error("WebAuthn non supporté sur cet appareil.");
  // Défi préparé à l'ouverture de l'écran (passkeyDefi.js) : au geste, la
  // biométrie part sans attendre le réseau — iOS refuse une cérémonie lancée
  // plus de quelques secondes après le geste (incident du 9 septembre). Sans
  // précharge, on le demande ici comme avant.
  const { obtenirDefi, consommerDefi } = await import("./passkeyDefi");
  const challenge = await obtenirDefi();
  const def = { challenge };
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fn = getFunctions(undefined, REGION_FRANCFORT);

  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: b64urlDecode(def.challenge),
      rpId: window.location.hostname,
      allowCredentials: [],           // ← découverte : aucun identifiant à saisir
      userVerification: "required",   // biométrie/PIN obligatoire
      timeout: 60000,
    },
  });
  if (!assertion) throw new Error("Authentification annulée.");

  const r = assertion.response;
  let data;
  try {
    ({ data } = await httpsCallable(fn, "passkeyLogin")({
      challenge: def.challenge,
      assertion: {
        credentialId: b64url(assertion.rawId),
        authenticatorData: b64url(r.authenticatorData),
        clientDataJSON: b64url(r.clientDataJSON),
        signature: b64url(r.signature),
        userHandle: r.userHandle ? new TextDecoder().decode(r.userHandle) : null,
      },
    }));
  } finally {
    consommerDefi(def.challenge); // usage unique côté serveur, succès ou refus
  }
  return data; // { ok, firebaseToken, profile }
};

// Produit la preuve biométrique attendue par reinitFinaliser (second facteur
// d'une réinitialisation de mot de passe). Défi émis par le serveur avec un
// usage « reinit » : il ne peut pas servir à ouvrir une session.
export const assertionPasskeyPourReinit = async () => {
  if (!webAuthnSupported()) throw new Error("WebAuthn non supporté sur cet appareil.");
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fn = getFunctions(undefined, REGION_FRANCFORT);
  const { data: def } = await httpsCallable(fn, "passkeyChallenge")({ usage: "reinit" });
  if (!def?.challenge) throw new Error("Défi indisponible — réessayez.");
  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: b64urlDecode(def.challenge),
      rpId: window.location.hostname,
      allowCredentials: [],
      userVerification: "required",
      timeout: 60000,
    },
  });
  if (!assertion) throw new Error("Vérification annulée.");
  const r = assertion.response;
  return {
    challenge: def.challenge,
    assertion: {
      credentialId: b64url(assertion.rawId),
      authenticatorData: b64url(r.authenticatorData),
      clientDataJSON: b64url(r.clientDataJSON),
      signature: b64url(r.signature),
      userHandle: r.userHandle ? new TextDecoder().decode(r.userHandle) : null,
    },
  };
};

// Valeurs canoniques d'un statut d'invite réunion (alignées sur le formulaire
// de réponse email /repondre) :
//   confirme   = sera présent(e) physiquement
//   visio      = participe en visio
//   excuse     = ne peut pas venir
//   provisoire = pas encore répondu / réponse conservatoire
//
// Historique : l'UI a utilisé "present"/"absent" et "accepte"/"refuse" en
// parallèle, créant 3 vocabulaires incompatibles. Cet helper normalise
// n'importe quelle valeur vers les 4 canoniques.
export const normaliserInviteStatut = (s) => {
  if (!s) return "provisoire";
  const v = String(s).toLowerCase();
  if (v === "confirme" || v === "present" || v === "accepte") return "confirme";
  if (v === "visio" || v === "distanciel") return "visio";
  if (v === "excuse" || v === "absent" || v === "refuse") return "excuse";
  return "provisoire"; // en_attente, provisoire, ou inconnu
};

// Liste plate pour les selects
export const getExpertisesList = (config) => {
  const custom = config?.expertises || {};
  // Si le config a une liste plate (array), l'utiliser ; sinon merger avec les défauts
  if (Array.isArray(custom)) return custom;
  // Merger les catégories custom + défaut
  const merged = {...EXPERTISES_DEFAUT};
  Object.entries(custom).forEach(([cat, items]) => {
    if (Array.isArray(items)) merged[cat] = items;
  });
  return merged;
};

// Créer une notification Firestore
export const creerNotification = async (type, payload) => {
  try {
    const { collection, addDoc } = await import("firebase/firestore");
    const { db } = await import("./firebase");
    await addDoc(collection(db, "notifications"), {
      type, ...payload, ts: Date.now(), read: false,
    });
  } catch (e) { console.error("creerNotification:", e.message); }
};

// ─────────────────────────────────────────────────────────────────────────────
// Partagés App ↔ SuperAdminPanel (Révision S1 tranches 9-10) :
// getMutuelles (+ MUTUELLES_DEFAULT interne), loadOTPAuth, FriseProjet
// (+ PHASES_DEFAUT / MOIS_LABELS / MonthPicker internes), ProjetFicheStructuree,
// STATUT_CONFIG, getAutoLists (DemandesPanel : parti dans
// DemandesEngagementView.js, lot L11d-bis). Déplacement pur depuis App.jsx,
// seuls les préfixes `export` ont été ajoutés.
// ─────────────────────────────────────────────────────────────────────────────

// Liste de fallback UNIQUEMENT utilisée si memberLists ET config.mutuelles sont
// vides (cas fresh install ou panne Firestore). Doit refléter les noms
// canoniques du seed (seed_mutuelles.js).
const MUTUELLES_DEFAULT = [
  "Amellis Mutuelles", "Carel Mutuelle", "CMAM", "HEYME",
  "Intégrance", "Intériale", "MGPS", "Mutami",
  "Mutuale", "Mutuelle de la Corse", "SMH", "Tutélaire", "UMCL",
];
// La liste effective est chargée depuis Firebase.
// Refonte 2026-05 (audit Phase 2 F.1) : memberLists est désormais la SOURCE
// DE VÉRITÉ PRIORITAIRE. Si lists est fourni et non vide, on l'utilise ;
// sinon fallback config.mutuelles (legacy), sinon MUTUELLES_DEFAULT.
// La signature accepte 2 formes pour rétro-compatibilité :
//   • getMutuelles(config)         → ancien usage, lit config.mutuelles
//   • getMutuelles(config, lists)  → nouveau, lit lists prioritairement
// Tous les composants doivent migrer vers la forme 2-args pour bénéficier
// de la source unique. Les composants qui n'ont pas accès à lists continueront
// à lire config.mutuelles temporairement (jusqu'à refactor complet).

export const getMutuelles = (config, lists = null) => {
  // Priorité 1 : memberLists si fourni et non vide.
  // Refonte 2026-05-21 : déduplication via normalisation NFD + alias
  // (mutuellesHelper.MUTUELLE_ALIAS) → règle le doublon historique UMGP / HEYME
  // et autres variantes orthographiques (Mutlog / UMCL, MGAS / MGPS, etc.).
  if (Array.isArray(lists) && lists.length > 0) {
    const seenCanonical = new Set();
    const out = [];
    for (const l of lists) {
      const name = l && l.name;
      if (!name || String(name).toLowerCase().includes("enio")) continue;
      const norm = normalizeMutuelleName(name);
      const alias = MUTUELLE_ALIAS[norm];
      const canonical = alias || name;
      const canonicalNorm = normalizeMutuelleName(canonical);
      if (seenCanonical.has(canonicalNorm)) continue;
      seenCanonical.add(canonicalNorm);
      out.push(canonical);
    }
    if (out.length > 0) return out.sort((a, b) => a.localeCompare(b, "fr"));
  }
  // Priorité 2 : config.mutuelles (legacy)
  const m = config?.mutuelles;
  if (Array.isArray(m) && m.length > 0) {
    // Même déduplication via alias appliquée à la liste legacy
    const seenCanonical = new Set();
    const out = [];
    for (const name of m) {
      if (!name) continue;
      const norm = normalizeMutuelleName(name);
      const alias = MUTUELLE_ALIAS[norm];
      const canonical = alias || name;
      const canonicalNorm = normalizeMutuelleName(canonical);
      if (seenCanonical.has(canonicalNorm)) continue;
      seenCanonical.add(canonicalNorm);
      out.push(canonical);
    }
    return out.sort((a, b) => a.localeCompare(b, "fr"));
  }
  // Fallback ultime
  return MUTUELLES_DEFAULT;
};

// otpauth vient du paquet npm (import paresseux, le chunk de MonProfilView et
// de totpEnrolement), jamais d'un CDN : le secret TOTP naît dans cette
// bibliothèque (briefing de sécurité du 27/10/2026, src/bibliothequesServies.js).
export const loadOTPAuth = (cb) => {
  import("otpauth").then(
    (OTPAuth) => cb(OTPAuth),
    (e) => console.error("Impossible de charger otpauth", e),
  );
};

export const FriseProjet = ({projet, meetings, isAdmin, onSave, onSaveAlertes, onAddReunion}) => {
  // Un seul state local — initialisé depuis projet au montage (key= force remontage après save)
  const initPhases = () => projet.phases || PHASES_DEFAUT.map(p=>({...p,active:false,debut:"",fin:""}));
  const [localPhases, setLocalPhases] = useState(initPhases);
  const [localDebut,  setLocalDebut]  = useState(projet.dateDebut||"");
  const [localFin,    setLocalFin]    = useState(projet.dateFin||"");
  const [editMode,    setEditMode]    = useState(false);
  const [alerteMode,  setAlerteMode]  = useState(false);
  const [popover,     setPopover]     = useState(null);
  const [scaleMode,   setScaleMode]   = useState("auto"); // auto | 1m | 6m | 1an | 3ans
  const [aDate,       setADate]       = useState("");
  const [aComm,       setAComm]       = useState("");
  const alertes = projet.alertesFrise || [];

  // Affichage ET édition utilisent localPhases/localDebut/localFin
  // Le composant est remonté (key change) après chaque sauvegarde Firestore.
  // Point 8 : une phase active SANS date de fin (ex. Déploiement / Disponible)
  // reste visible (barre « ouverte » jusqu'au bord droit), il suffit d'un début.
  const phasesActives = localPhases.filter(p=>p.active&&p.debut);

  // Point 7 : on ne PROPOSE plus que ces 4 phases dans l'éditeur. Les phases
  // d'un autre type déjà actives sur un projet existant restent affichées et
  // éditables (aucune donnée perdue) — voir filtre de l'éditeur ci-dessous.
  const PHASES_OFFERTES = ["exploration", "negociation", "deploiement", "disponible"];

  const today = new Date().toISOString().slice(0,7);
  const finEffective = localFin || today;

  // Étapes datées de la fiche (prochainesEtapes) → marqueurs sur la frise
  const ETAPE_META = {
    a_venir:  { icon: "➝",  color: "#9CA3AF", label: "À venir" },
    en_cours: { icon: "⏱️", color: "#F49800", label: "En cours" },
    fait:     { icon: "✅", color: "#94C11F", label: "Réalisé" },
  };
  const datedEtapes = (projet.prochainesEtapes || [])
    .filter(e => e && e.date && /^\d{4}-\d{2}/.test(e.date));
  const hasEtapes = datedEtapes.length > 0;

  const allDates = [
    ...(localDebut?[localDebut]:[]),
    ...[finEffective],
    ...phasesActives.flatMap(p=>[p.debut,p.fin]),
    ...meetings.map(m=>(m.date||"").slice(0,7)).filter(Boolean),
    ...alertes.map(a=>a.date).filter(Boolean),
    ...datedEtapes.map(e=>e.date.slice(0,7)),
  ].filter(Boolean).sort();

  const axeDebut = allDates[0] || today;
  const axeFin   = allDates[allDates.length-1] || today;

  const toMois = (ym) => {
    if(!ym) return 0;
    const [y,m] = ym.split("-").map(Number);
    return y*12 + (m||1);
  };
  const debutMois = toMois(axeDebut);
  const finMois   = toMois(axeFin);
  const totalMois = Math.max(finMois - debutMois, 1);

  // Échelle SÉLECTIONNABLE (point 9) : « Ajustée » (auto, tout tient ~880px)
  // ou zoom fixe 1 mois / 6 mois / 1 an / 3 ans en px/mois → la frise déborde
  // et défile latéralement (conteneur overflow-x:auto plus bas).
  const SCALE_PX = { "1m": 150, "6m": 80, "1an": 42, "3ans": 15 };
  const PX_PAR_MOIS = scaleMode === "auto"
    ? Math.max(18, Math.min(130, Math.round(880 / Math.max(totalMois, 1))))
    : (SCALE_PX[scaleMode] || 42);
  const axeW = Math.max(totalMois * PX_PAR_MOIS, 460);

  const xOf = (ym) => {
    const pct = Math.max(0, Math.min(1, (toMois(ym) - debutMois) / totalMois));
    return 20 + axeW * pct;
  };

  const reunionsLiees = meetings.filter(m=>(m.projetsLies||[]).includes(projet.id));

  const W = axeW + 40;

  // Labels axe
  const labelsAxe = [];
  for(let m = debutMois; m <= finMois; m++) {
    const mo = ((m-1)%12)+1;
    const yr = Math.floor((m-1)/12);
    const isJan = mo===1;
    if(isJan||mo===4||mo===7||mo===10||m===debutMois||m===finMois) {
      const pct = (m-debutMois)/totalMois;
      const label = isJan?`${yr}`:`${String(mo).padStart(2,"0")}/${String(yr).slice(-2)}`;
      labelsAxe.push({x: 20+axeW*pct, label, bold:isJan});
    }
  }

  // Hauteur SVG : marqueurs (haut) + axe + voie étapes (si présentes) + phases.
  const phaseY0 = 88 + (hasEtapes ? 30 : 0);
  const svgHeight = Math.max(100, (hasEtapes ? 100 : 70) + phasesActives.length * 34);

  const fmtMois = (ym) => {
    if(!ym) return "—";
    const [y,m] = ym.split("-");
    return `${MOIS_LABELS[parseInt(m)-1]} ${y}`;
  };

  const saveAlerte = async () => {
    if(!aDate||!aComm.trim()) return;
    const nouv = [...alertes, {id:Date.now().toString(36), date:aDate, commentaire:aComm.trim()}];
    await onSaveAlertes(nouv);
    setADate(""); setAComm(""); setAlerteMode(false);
  };

  const supprimerAlerte = async (id) => {
    await onSaveAlertes(alertes.filter(a=>a.id!==id));
  };

  return(
    <div style={{marginTop:14}} onClick={()=>setPopover(null)}>

      {/* En-tête */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <div style={{fontWeight:700,fontSize:"var(--fs-micro)",color:"var(--text-sub)",
          fontFamily:"Montserrat,sans-serif",letterSpacing:"calc(.04em * var(--em-ajuste, 1))"}}>
          FRISE DU PROJET
        </div>
        {isAdmin&&(
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {onAddReunion&&(
              <button className="btn btn-ghost btn-sm"
                onClick={e=>{e.stopPropagation();onAddReunion();}}>
                <IconeTexte nom="calendar"/> Réunion
              </button>
            )}
            <button className="btn btn-ghost btn-sm"
              onClick={e=>{e.stopPropagation();setAlerteMode(o=>!o);}}>
              {alerteMode?"Fermer":<><IconeTexte nom="alert-triangle" couleur="var(--tx-orange)"/> Alerte</>}
            </button>
            <button className="btn btn-ghost btn-sm"
              onClick={e=>{e.stopPropagation();setEditMode(o=>!o);}}>
              {editMode?"Fermer":<><IconeTexte nom="settings"/> Phases</>}
            </button>
          </div>
        )}
      </div>

      {/* Sélecteur d'échelle (point 9) — zoom + défilement horizontal */}
      <div style={{display:"flex",gap:4,alignItems:"center",marginBottom:8,flexWrap:"wrap"}}>
        <span style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",fontWeight:600}}>Échelle :</span>
        {[["auto","Ajustée"],["1m","1 mois"],["6m","6 mois"],["1an","1 an"],["3ans","3 ans"]].map(([k,l])=>(
          <button key={k} type="button" onClick={e=>{e.stopPropagation();setScaleMode(k);}}
            style={{fontSize:"var(--fs-micro)",fontWeight:700,padding:"3px 9px",borderRadius:"var(--radius-pill)",cursor:"pointer",
              border:"1px solid "+(scaleMode===k?"#003F59":"var(--border-color)"),
              background:scaleMode===k?"#003F59":"var(--bg-card,#fff)",
              color:scaleMode===k?"#fff":"var(--text-sub)"}}>{l}</button>
        ))}
      </div>

      {/* Formulaire alerte */}
      {alerteMode&&isAdmin&&(
        <div style={{background:"#FFF8E1",border:"1px solid #F49800",borderRadius:"var(--radius-md)",
          padding:"12px 14px",marginBottom:12}} onClick={e=>e.stopPropagation()}>
          <div style={{fontWeight:700,fontSize:"var(--fs-micro)",color:"#92400E",marginBottom:10,
            fontFamily:"Montserrat,sans-serif"}}><IconeTexte nom="alert-triangle" couleur="var(--acc-orange-ink)"/> AJOUTER UNE ALERTE</div>
          <div style={{display:"flex",gap:10,marginBottom:10,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:140}}>
              <label className="lbl">Date (mois)</label>
              <MonthPicker value={aDate} onChange={setADate}/>
            </div>
            <div style={{flex:2,minWidth:200}}>
              <label className="lbl">Commentaire</label>
              <input className="inp" value={aComm}
                onChange={e=>setAComm(e.target.value)}
                placeholder="Expliquer l'alerte..."/>
            </div>
          </div>
          <button className="btn btn-orange btn-sm"
            disabled={!aDate||!aComm.trim()} onClick={saveAlerte}>
            Ajouter
          </button>
          {alertes.length>0&&(
            <div style={{marginTop:10}}>
              {alertes.map(a=>(
                <div key={a.id} style={{display:"flex",alignItems:"center",gap:8,
                  fontSize:"var(--fs-micro)",padding:"4px 0",borderTop:"0.5px solid #F49800"}}>
                  <span style={{color:"var(--acc-orange-ink)"}}><IconeTexte nom="alert-triangle" titre="Attention"/></span>
                  <span style={{fontWeight:600,color:"#92400E"}}>{fmtMois(a.date)}</span>
                  <span style={{flex:1,color:"#78350F"}}>{a.commentaire}</span>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:"var(--fs-micro)",color:"var(--acc-red-ink)"}}
                    onClick={()=>supprimerAlerte(a.id)}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mode édition phases */}
      {editMode&&isAdmin&&(
        <div style={{background:"var(--bg-card-inner)",borderRadius:"var(--radius-md)",
          padding:"14px 16px",marginBottom:14}} onClick={e=>e.stopPropagation()}>
          <div style={{display:"flex",gap:12,marginBottom:14,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:130}}>
              <label className="lbl">Date début projet</label>
              <MonthPicker value={localDebut} onChange={setLocalDebut}/>
            </div>
            <div style={{flex:1,minWidth:130}}>
              <label className="lbl">Date fin projet</label>
              <MonthPicker value={localFin} onChange={setLocalFin}/>
            </div>
          </div>
          <div style={{fontWeight:600,fontSize:"var(--fs-micro)",color:"var(--text-sub)",
            marginBottom:8,fontFamily:"Montserrat,sans-serif"}}>PHASES</div>
          {localPhases.map((ph,i)=>(
            (PHASES_OFFERTES.includes(ph.id) || ph.active) ? (
            <div key={ph.id} style={{display:"flex",alignItems:"center",
              gap:8,marginBottom:8,flexWrap:"wrap"}}>
              <input type="checkbox" checked={!!ph.active}
                onChange={e=>setLocalPhases(ps=>ps.map((p,j)=>
                  j===i?{...p,active:e.target.checked}:p
                ))}
                style={{flexShrink:0,width:14,height:14,accentColor:ph.couleur}}/>
              <div style={{width:10,height:10,borderRadius:"50%",
                background:ph.couleur,flexShrink:0}}/>
              <input className="inp" value={ph.label}
                onChange={e=>setLocalPhases(ps=>ps.map((p,j)=>
                  j===i?{...p,label:e.target.value}:p
                ))}
                style={{width:110,fontSize:"var(--fs-input-dense,11px)",padding:"3px 8px"}}/>
              <MonthPicker
                value={ph.debut||""} disabled={!ph.active}
                onChange={v=>setLocalPhases(ps=>ps.map((p,j)=>j===i?{...p,debut:v}:p))}
                style={{flex:1,minWidth:110}}/>
              <span style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)"}}>→</span>
              <MonthPicker
                value={ph.fin||""} disabled={!ph.active}
                onChange={v=>setLocalPhases(ps=>ps.map((p,j)=>j===i?{...p,fin:v}:p))}
                style={{flex:1,minWidth:110}}/>
            </div>
            ) : null
          ))}
          {(()=>{
            const errPhases=localPhases.filter(p=>p.active&&p.debut&&p.fin&&p.debut>p.fin);
            const errGlobal=localDebut&&localFin&&localDebut>localFin;
            const hasErr=errPhases.length>0||errGlobal;
            return(<>
              {errGlobal&&<div style={{fontSize:"var(--fs-micro)",color:"var(--tx-red)",marginTop:6,fontWeight:600}}>
                <IconeTexte nom="alert-triangle" couleur="var(--tx-orange)"/> La date de fin doit être après le début du projet.</div>}
              {errPhases.map(p=><div key={p.id} style={{fontSize:"var(--fs-micro)",color:"var(--tx-red)",marginTop:4,fontWeight:600}}>
                <IconeTexte nom="alert-triangle" couleur="var(--tx-orange)"/> Phase "{p.label}" : fin avant début.</div>)}
              <button className="btn btn-orange btn-sm" style={{marginTop:8}}
                disabled={hasErr}
                onClick={async()=>{
                  await onSave(localPhases, localDebut, localFin);
                  setEditMode(false);
                }}>
                Enregistrer
              </button>
            </>);
          })()}
        </div>
      )}

      {/* SVG Frise */}
      {(phasesActives.length>0||reunionsLiees.length>0||alertes.length>0||localDebut)&&(
        <div style={{position:"relative",overflowX:"auto"}}>
          <svg viewBox={`0 0 ${W} ${svgHeight}`} width={W} height={svgHeight}
            style={{fontFamily:"Montserrat,sans-serif", display:"block"}}
            onClick={e=>{e.stopPropagation();setPopover(null);}}>

            {/* Rail axe */}
            <rect x={20} y={58} width={axeW} height={3} rx={1.5}
              fill="var(--border-color,#334155)" opacity={0.3}/>

            {/* Labels axe */}
            {labelsAxe.map((l,i)=>(
              <g key={i}>
                <line x1={l.x} y1={52} x2={l.x} y2={64}
                  stroke="var(--border-color,#e5e7eb)" strokeWidth={l.bold?1.5:0.5}/>
                <text x={l.x} y={73} fontSize={l.bold?9:7.5} textAnchor="middle"
                  fill={l.bold?"var(--text-main,#fff)":"var(--text-sub,#8a9aaa)"}
                  fontWeight={l.bold?700:400}>
                  {l.label}
                </text>
              </g>
            ))}

            {/* Phases — décalées verticalement */}
            {phasesActives.map((ph,i)=>{
              const openEnded = !ph.fin;
              const x1 = xOf(ph.debut);
              const x2 = xOf(ph.fin || axeFin);   // phase ouverte → jusqu'au bord droit
              const y  = phaseY0 + i*34;
              const w  = Math.max(x2-x1, openEnded ? 14 : 3);
              return(
                <g key={ph.id} style={{cursor:"pointer"}}
                  onClick={e=>{
                    e.stopPropagation();
                    setPopover({
                      x: (x1+x2)/2, y: y-16,
                      lines:[ph.label, `${fmtMois(ph.debut)} → ${openEnded ? "en cours" : fmtMois(ph.fin)}`],
                      color: ph.couleur
                    });
                  }}>
                  {/* Tiret vertical depuis l'axe */}
                  <line x1={x1} y1={61} x2={x1} y2={y-8}
                    stroke={ph.couleur} strokeWidth={1} strokeDasharray="3,2" opacity={0.5}/>
                  {/* Bande phase — pour une phase ouverte, on estompe le bord droit */}
                  <rect x={x1} y={y-12} width={w} height={24} rx={12}
                    fill={ph.couleur} opacity={openEnded ? 0.7 : 0.88}
                    strokeDasharray={openEnded ? "1,0" : undefined}/>
                  {openEnded&&(
                    <text x={x2-9} y={y+4} fontSize={12} textAnchor="middle" fill="#fff" fontWeight={800}>›</text>
                  )}
                  {/* Label si assez large */}
                  {w>40&&(
                    <text x={openEnded ? x1+8 : (x1+x2)/2} y={y+4} fontSize={9}
                      textAnchor={openEnded ? "start" : "middle"}
                      fill="#fff" fontWeight={600}>{ph.label}</text>
                  )}
                  {/* Point de début */}
                  <circle cx={x1} cy={60} r={3.5} fill={ph.couleur}/>
                </g>
              );
            })}

            {/* Marqueurs réunions */}
            {reunionsLiees.map((m,i)=>{
              const mois=(m.date||"").slice(0,7);
              if(!mois) return null;
              const x=xOf(mois);
              return(
                <g key={m.id||i} style={{cursor:"pointer"}}
                  onClick={e=>{
                    e.stopPropagation();
                    setPopover({x,y:24,
                      lines:[`📅 ${m.intitule||"Réunion"}`,m.date+(m.duree?" · "+m.duree:"")],
                      color:"#2AA9E1"
                    });
                  }}>
                  <line x1={x} y1={32} x2={x} y2={62}
                    stroke="#2AA9E1" strokeWidth={1.5} strokeDasharray="2,2"/>
                  <circle cx={x} cy={24} r={9} fill="#2AA9E1"/>
                  <text x={x} y={28} fontSize={9} textAnchor="middle" fill="#fff"></text>
                </g>
              );
            })}

            {/* Marqueurs alertes */}
            {alertes.map((a,i)=>{
              if(!a.date) return null;
              const x=xOf(a.date);
              return(
                <g key={a.id||i} style={{cursor:"pointer"}}
                  onClick={e=>{
                    e.stopPropagation();
                    setPopover({x,y:20,
                      lines:["⚠ Alerte",a.commentaire],
                      color:"#F49800"
                    });
                  }}>
                  <line x1={x} y1={28} x2={x} y2={57}
                    stroke="#F49800" strokeWidth={1.5} strokeDasharray="2,2"/>
                  <circle cx={x} cy={22} r={8} fill="#F49800"/>
                  <text x={x} y={26} fontSize={11} textAnchor="middle" fill="#fff"></text>
                </g>
              );
            })}

            {/* Marqueurs étapes (fiche) — voie sous l'axe */}
            {datedEtapes.map((e,i)=>{
              const x=xOf((e.date||"").slice(0,7));
              const meta = ETAPE_META[e.statut] || ETAPE_META.a_venir;
              const dateFr = new Date(e.date+"T12:00").toLocaleDateString("fr-FR",
                {day:"2-digit",month:"short",year:"numeric"});
              return(
                <g key={"et"+i} style={{cursor:"pointer"}}
                  onClick={ev=>{
                    ev.stopPropagation();
                    setPopover({x,y:64,
                      lines:[`${meta.icon} ${e.texte||"Étape"}`, `${meta.label} · ${dateFr}`],
                      color:meta.color});
                  }}>
                  <line x1={x} y1={62} x2={x} y2={74}
                    stroke={meta.color} strokeWidth={1.2} strokeDasharray="2,2"/>
                  <circle cx={x} cy={82} r={8} fill={meta.color}/>
                  <text x={x} y={86} fontSize={9} textAnchor="middle" fill="#fff">{meta.icon}</text>
                </g>
              );
            })}

            {/* Popover */}
            {popover&&(()=>{
              const pw=220;
              const lineCount = popover.lines.length;
              // Estimer la hauteur : titre + lignes de texte wrappées
              const maxChars = Math.max(...popover.lines.map(l=>(l||"").length));
              const wrapLines = Math.ceil(maxChars / 28); // ~28 chars par ligne à fontSize 9
              const ph2 = Math.max(38, 16 + (lineCount > 1 ? 16 : 0) + wrapLines * 12);
              const px=Math.min(Math.max(popover.x-pw/2, 4), W-pw-4);
              const py=Math.max(popover.y-ph2-6, 4);
              return(
                <g onClick={e=>e.stopPropagation()}>
                  <rect x={px} y={py} width={pw} height={ph2} rx={6}
                    fill="var(--bg-app,#04202d)" stroke={popover.color||"var(--border-color)"}
                    strokeWidth={1.5}/>
                  <foreignObject x={px+8} y={py+4} width={pw-16} height={ph2-8}>
                    <div xmlns="http://www.w3.org/1999/xhtml" style={{fontFamily:"Montserrat,sans-serif"}}>
                      {popover.lines.map((line,i)=>(
                        <div key={i} style={{
                          fontSize:"var(--fs-micro)",
                          fontWeight:i===0?700:400,
                          color:i===0?(navTexte(popover.color)||"var(--text-main)"):"var(--text-main,#fff)",
                          lineHeight:"1.3",
                          wordWrap:"break-word",
                          overflowWrap:"break-word",
                          marginBottom:i===0?2:0
                        }}>{line}</div>
                      ))}
                    </div>
                  </foreignObject>
                </g>
              );
            })()}
          </svg>
        </div>
      )}

      {!phasesActives.length&&!localDebut&&!reunionsLiees.length&&!alertes.length&&(
        <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",fontStyle:"italic",padding:"8px 0"}}>
          {isAdmin
            ?<>Aucune phase définie : cliquez sur <IconeTexte nom="settings"/> Phases pour commencer.</>
            :"Frise non encore configurée pour ce projet."}
        </div>
      )}

      {/* Liste réunions liées */}
      {reunionsLiees.length>0&&(
        <div style={{marginTop:10}}>
          <div style={{fontSize:"var(--fs-micro)",fontWeight:700,color:"var(--text-sub)",
            letterSpacing:"calc(.04em * var(--em-ajuste, 1))",marginBottom:6,fontFamily:"Montserrat,sans-serif"}}>
            RÉUNIONS LIÉES
          </div>
          {reunionsLiees.map(m=>(
            <div key={m.id} style={{display:"flex",alignItems:"center",gap:8,
              fontSize:"var(--fs-micro)",color:"var(--text-main)",marginBottom:4}}>
              <span style={{color:"var(--tx-blue)"}}><IconeTexte nom="calendar" titre="Date"/></span>
              <span style={{fontWeight:600}}>{m.date}</span>
              <span style={{color:"var(--text-sub)"}}>{m.intitule}</span>
              {m.duree&&<span style={{color:"var(--text-sub)",fontSize:"var(--fs-micro)"}}>· {m.duree}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Phases de projet ──────────────────────────────────────────────────────────
const PHASES_DEFAUT = [
  {id:"exploration",  label:"Exploration",   couleur:"#6366F1"},
  {id:"negociation",  label:"Négociation",   couleur:"#F59E0B"},
  {id:"cadrage",      label:"Cadrage",       couleur:"#3B82F6"},
  {id:"pilote",       label:"Pilote",        couleur:"#8B5CF6"},
  {id:"production",   label:"Production",    couleur:"#EC4899"},
  {id:"deploiement",  label:"Déploiement",   couleur:"#10B981"},
  {id:"mise_en_place",label:"Mise en place", couleur:"#14B8A6"},
  {id:"disponible",   label:"Disponible",    couleur:"#22C55E"},
  {id:"bilan",        label:"Bilan",         couleur:"#6B7280"},
];



// ── Sélecteur mois/année (remplace input type="month" non supporté sur Safari) ─
const MOIS_LABELS = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];
const MonthPicker = ({value, onChange, disabled=false, style={}}) => {
  // value = "YYYY-MM" ou ""
  const yr  = value ? parseInt(value.split("-")[0]) : "";
  const mo  = value ? parseInt(value.split("-")[1]) : "";
  const now = new Date();
  const years = [];
  for(let y = now.getFullYear()-5; y <= now.getFullYear()+10; y++) years.push(y);

  const emit = (newYr, newMo) => {
    if(newYr && newMo) onChange(`${newYr}-${String(newMo).padStart(2,"0")}`);
    else onChange("");
  };

  return(
    <div style={{display:"flex",gap:4,...style}}>
      <select className="inp" value={mo||""} disabled={disabled}
        onChange={e=>emit(yr||now.getFullYear(), e.target.value)}
        style={{flex:1,fontSize:"var(--fs-input-dense,11px)",padding:"3px 4px"}}>
        <option value="">Mois</option>
        {MOIS_LABELS.map((l,i)=>(
          <option key={i+1} value={i+1}>{l}</option>
        ))}
      </select>
      <select className="inp" value={yr||""} disabled={disabled}
        onChange={e=>emit(e.target.value, mo||1)}
        style={{flex:1,fontSize:"var(--fs-input-dense,11px)",padding:"3px 4px"}}>
        <option value="">Année</option>
        {years.map(y=>(
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
};

// ── Affichage public de la fiche structurée d'un projet ────────────────────
// Lit les champs contexte/objectifs/methodologie/principes/livrables/
// beneficesAttendus/prochainesEtapes du projet et les rend dans le style
// de nos slides (bullets bold, sections bien séparées). Utilisé sur la fiche
// projet expandée d'Athos et réutilisé tel quel en mini-aperçu pour
// "Préparer une présentation".
export function ProjetFicheStructuree({ projet }) {
  if (!projet) return null;
  const ctx = (projet.contexte || "").trim();
  const objs = (projet.objectifs || []).filter(s => (s||"").trim());
  const methos = (projet.methodologie || []).filter(s => (s||"").trim());
  const princ = (projet.principes || []).filter(s => (s||"").trim());
  // Livrables et étapes : items typés {texte, statut, date, niveau} (avec
  // backward compat string→objet via normalizeItem). Statut par défaut diffère :
  // les livrables legacy étaient des notes statiques (donc fait), les étapes
  // étaient à venir.
  const livs = (projet.livrables || [])
    .map(it => normalizeItem(it, "fait"))
    .filter(it => it && (it.texte||"").trim());
  const benefs = (projet.beneficesAttendus || []).filter(s => (s||"").trim());
  const etapes = (projet.prochainesEtapes || [])
    .map(it => normalizeItem(it, "a_venir"))
    .filter(it => it && (it.texte||"").trim());
  const hasContent = ctx || objs.length || methos.length || princ.length ||
                     livs.length || benefs.length || etapes.length;
  if (!hasContent) return null;

  // Section colorée — chaque catégorie a sa propre couleur d'accent
  // pour un repérage visuel rapide
  const colored = (color, label, content) => (
    <div style={{marginBottom:14}}>
      <div style={{fontSize:"var(--fs-micro)",fontWeight:700,color: navTexte(color),
        marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
        <span style={{width:14,height:2,background:color,borderRadius:"var(--radius-pill)"}}/>
        {label}
      </div>
      <div style={{paddingLeft:8}}>{content}</div>
    </div>
  );
  const bulletsCol = (items, color) => (
    <ul style={{margin:0,paddingLeft:18,listStyle:"none"}}>
      {items.map((it, i) => {
        const rich = isAmdtHtml(it);
        return (
          <li key={i} style={{position:"relative",fontSize:"var(--fs-meta)",lineHeight:1.6,
            color:"var(--text-main)",marginBottom:5,paddingLeft:2}}>
            <span style={{position:"absolute",left:-14,color: navTexte(color),fontWeight:700,fontSize:"var(--fs-nav)",lineHeight:1.4}}>•</span>
            {rich
              ? <div className="rich-text" style={{display:"inline-block",verticalAlign:"top",width:"100%"}}
                  dangerouslySetInnerHTML={{__html:sanitizeAmdtHtml(it)}}/>
              : it}
          </li>
        );
      })}
    </ul>
  );

  // Rendu d'une liste d'items typés (livrables / étapes) avec statut + date
  // + sous-points (niveau:1 indenté). Mutualisé pour les deux sections.
  const itemsTypes = (items, accentColor) => (
    <div style={{display:"flex",flexDirection:"column",gap:5,paddingLeft:8}}>
      {items.map((e, i) => {
        const meta = ITEM_STATUT_META[e.statut] || ITEM_STATUT_META.a_venir;
        const isSub = e.niveau === 1;
        return (
          <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",
            fontSize:"var(--fs-micro)",padding:"6px 10px",
            borderRadius:"var(--radius-sm)",
            marginLeft: isSub ? 24 : 0,
            // Surface du thème, sans liseré (lot L11d) : le statut se lit dans
            // son libellé coloré. Sous-points : fond légèrement teinté.
            backgroundColor: isSub ? "var(--card-inner,rgba(0,0,0,.02))" : "var(--bg-card)"}}>
            {isSub && (
              <span style={{color:"var(--text-sub)",fontSize:"var(--fs-micro)",paddingTop:1,flexShrink:0}}>↳</span>
            )}
            <span style={{fontSize:"var(--fs-micro)",fontWeight:700,color:navTexte(meta.color),flexShrink:0,
              minWidth:80,letterSpacing:"calc(.02em * var(--em-ajuste, 1))"}}>
              {meta.label}
            </span>
            <span style={{flex:1,color:"var(--text-main)",lineHeight:1.5}}>{e.texte}</span>
            {e.date && (
              <span style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",flexShrink:0,
                whiteSpace:"nowrap",alignSelf:"center"}}>
                {new Date(e.date+"T12:00").toLocaleDateString("fr-FR",
                  {day:"2-digit",month:"short",year:"numeric"})}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{marginBottom:16,border:"1px solid var(--border-color)",
      borderRadius:"var(--radius-md)",padding:"14px 16px"}}>
      {/* Header de la section */}
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14,
        paddingBottom:"var(--filet-ecart)",borderBottom:"1px solid var(--border-color)"}}>
        <span style={{fontSize:"var(--fs-nav)"}}><IconeTexte nom="clipboard-list" titre="Liste"/></span>
        <span style={{fontSize:"var(--fs-meta)",fontWeight:600,color:"var(--text-main)"}}>Fiche projet</span>
      </div>

      {ctx && (
        <div style={{marginBottom:14,fontSize:"var(--fs-meta)",fontStyle:"italic",
          color:"var(--text-main)",lineHeight:1.6,
          padding:"10px 14px",background:"var(--card-inner)",
          borderRadius:"var(--radius-sm)"}}>
          {isAmdtHtml(ctx)
            ? <div className="rich-text" dangerouslySetInnerHTML={{__html:sanitizeAmdtHtml(ctx)}}/>
            : <div style={{whiteSpace:"pre-wrap"}}>{ctx}</div>}
        </div>
      )}
      {/* Teintes de la charte (NAV_COLORS) : navTexte les rend en encre AA
          du THÈME, lisible de jour comme de nuit (lot L11d : la fiche quitte
          son fond blanc codé en dur). Libellés sans emoji. */}
      {objs.length > 0 && colored(NAV_COLORS.mutuelles, "Objectifs", bulletsCol(objs, NAV_COLORS.mutuelles))}
      {methos.length > 0 && colored(NAV_COLORS.pilotage, "Méthodologie", bulletsCol(methos, NAV_COLORS.pilotage))}
      {princ.length > 0 && colored(NAV_COLORS.gouv, "Principes structurants", bulletsCol(princ, NAV_COLORS.gouv))}
      {livs.length > 0 && colored(NAV_COLORS.athos, "Livrables attendus", itemsTypes(livs, NAV_COLORS.athos))}
      {benefs.length > 0 && colored(NAV_COLORS.rh, "Bénéfices attendus pour les mutuelles", bulletsCol(benefs, NAV_COLORS.rh))}
      {etapes.length > 0 && colored(NAV_COLORS.mutuelles, "Étapes", itemsTypes(etapes, NAV_COLORS.mutuelles))}
    </div>
  );
}

// STATUT_CONFIG : indexé UNIQUEMENT sur les valeurs canoniques. Pour lire
// un invite.statut, toujours passer par normaliserInviteStatut() d'abord.
export const STATUT_CONFIG = {
  confirme:   { label:"Présent(e)", color:"#14532D", bg:"#DCFCE7", emoji:"✅" },
  visio:      { label:"Visio",      color:"#1E3A8A", bg:"#DBEAFE", emoji:"💻" },
  excuse:     { label:"Excusé(e)",  color:"#92400E", bg:"#FEF3C7", emoji:"❌" },
  provisoire: { label:"Provisoire", color:"#6B7280", bg:"#F3F4F6", emoji:"⏳" },
};

// ============================================================================
//  LISTES AUTOMATIQUES (calculées depuis l'annuaire)
// ============================================================================
// Le Bureau (membresBureau) et les listes automatiques vivent dans
// src/listesDiffusion.js, la source unique des listes de diffusion, miroir de
// listesConnues (functions/lib/notesDiffusion.js) : mêmes identifiants, mêmes
// noms (« Bureau (automatique) »…), mêmes membres. Réexporté ici.
export { membresBureau };

/**
 * Les listes automatiques pour INVITER à une séance (participants, import) :
 * les instances et le Bureau, dans l'ordre canonique, membres au format des
 * participants. Sans `gouvernance`, pas de Bureau (jamais une liste fausse).
 * DOCTRINE (Pierre, 24-26/08) : une fin de mandat DÉPASSÉE ne retire jamais
 * un droit toute seule ; seule la sortie explicite (statut « sorti » ou
 * « termine ») fait foi. Filtrer sur la date vidait la liste AG à l'approche
 * d'un renouvellement, au moment où l'on convoque l'assemblée qui doit réélire.
 */
export const getAutoLists = (delegates, gouvernance = null) =>
  listesAutomatiques(listesDiffusion({ delegates, gouvernance })).map((l) => ({
    id: l.id,
    name: l.nom,
    instanceType: l.instanceType,
    auto: true,
    members: l.membres.map((d) => ({
      id: d.id,
      prenom: d.prenom,
      nom: d.nom,
      mutuelle: d.mutuelle || "",
      email: d.email || "",
      // Importer une liste, c'est INVITER : la présence se constate ensuite
      // (réponse à la convocation, émargement) ; elle ne se présume pas.
      statut: STATUT_DEFAUT,
    })),
  }));

// ─────────────────────────────────────────────────────────────────────────────
// Partagés App ↔ AthosReunionsView (Révision S1 tranche 11) :
// NootaImportPanelLazy (+ NootaImportPanelRaw interne) et consumeNavParams
// (deeplink helper navTo). Déplacement pur depuis App.jsx, seuls les préfixes
// `export` ont été ajoutés.
// ─────────────────────────────────────────────────────────────────────────────

const NootaImportPanelRaw = lazyRetry(() => import("./NootaImportPanel"));
export const NootaImportPanelLazy = (props) => (<Suspense fallback={<div style={{padding:20,textAlign:"center",fontSize:"var(--fs-micro)",color:"var(--text-sub)"}}>Chargement Noota…</div>}><NootaImportPanelRaw {...props}/></Suspense>);

// ─── Deeplink helper : consomme les params de navTo() côté module cible ─────
// Utilisé par les modules qui veulent auto-ouvrir un item précis quand on
// arrive via navTo(view, { projectId, reunionId, ... }).
// Sources lues, par ordre de priorité :
//   1. sessionStorage "aramis_nav_params" (navigation interne récente, TTL 10s)
//   2. URL query string (ex : copier-coller d'un lien direct, bookmark)
// Le consumer efface le payload sessionStorage pour éviter les re-ouvertures
// non voulues (ex : refresh de page). Les paramètres URL ne sont PAS effacés
// — l'utilisateur peut copier l'URL à nouveau.
export function consumeNavParams() {
  // 1. sessionStorage (nav interne)
  try {
    const s = sessionStorage.getItem("aramis_nav_params");
    if (s) {
      const p = JSON.parse(s);
      if (p && typeof p === "object") {
        // Expire après 10 s (évite que d'anciens params traînent sur refresh)
        if (!p._ts || Date.now() - p._ts <= 10000) {
          sessionStorage.removeItem("aramis_nav_params");
          return p;
        }
        sessionStorage.removeItem("aramis_nav_params");
      }
    }
  } catch {}
  // 2. URL query string (deep-link / partage)
  try {
    const qp = new URLSearchParams(window.location.search);
    const out = {};
    let any = false;
    for (const [k, v] of qp.entries()) {
      if (k === "view") continue; // géré par le routeur principal
      out[k] = v;
      any = true;
    }
    return any ? out : null;
  } catch { return null; }
}

// ─────────────────────────────────────────────────────────────────────────────
// Partagés App ↔ Admin (Révision S1 tranche 12) : loadJsPDF, sanitize, Prog,
// TYPE_LABELS, ScrutinResults (+ normalizeWordGlobal, helper interne) et
// DocumentLibrary. Déplacement pur depuis App.jsx, seuls les préfixes
// `export` ont été ajoutés.
// ─────────────────────────────────────────────────────────────────────────────

// jsPDF servi par Aramis, avec son empreinte (src/bibliothequesServies.js).
// Déjà chargé, le rappel part tout de suite, comme avant.
export const loadJsPDF = (cb) => {
  const deja = BIBLIOTHEQUES.jspdf.lire();
  if(deja){cb(deja);return;}
  chargerJsPDF().then(cb, (e) => console.error(e.message));
};

export const sanitize = (s) => (s||"").replace(/[\s/\\:*?"\x3C\x3E|]/g,"_").slice(0,40);

// -- Composants de base -------------------------------------------------------

export const Prog = ({value,max,color="#F49800",h=8}) => {
  const pct=max>0?Math.round(value/max*100):0;
  return <div style={{display:"flex",alignItems:"center",gap:10}}>
    <Jauge valeur={pct} couleur={color} piste="var(--ptrack-bg)" hauteur={h} style={{flex:1}} />
    <div style={{fontSize:"var(--fs-micro)",fontWeight:700,minWidth:32,textAlign:"right"}}>{pct}%</div>
  </div>;
};

export const TYPE_LABELS={yon:"Oui/Non/Abstention",nominatif:"Nominatif",liste:"Scrutin de liste",notation:"Notation",nuage:"Nuage de mots",swot:"SWOT",amendement:"Atelier d'amendements",info:"Point d'information",divers:"Divers"};

// -- ScrutinResults -----------------------------------------------------------
const normalizeWordGlobal=(s)=>s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").trim();

export const ScrutinResults = ({s,vs,quorum,isAdmin=false,onSave,appState,votesByScrutin}) => {
  const [swotEdit,setSwotEdit]=useState(null); // {id, quadrant, texte}

  if(!s.resultsPublished&&!isAdmin) return(
    <div className="results-hidden">
      <div style={{fontSize:"var(--fs-plaque)",marginBottom:8}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
      <div style={{fontWeight:700,fontSize:"var(--fs-meta)",color:navTexte(C.orangeL),marginBottom:5}}>Résultats non publiés</div>
      <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray)}}>Le président de séance n'a pas encore rendu les résultats publics.</div>
    </div>
  );

  // -- NUAGE DE MOTS --
  if(s.type==="nuage"){
    const allWords=[];
    vs.forEach(v=>{(v.words||[]).forEach(w=>allWords.push(w));});
    const counts={};
    allWords.forEach(w=>{const k=normalizeWordGlobal(w);if(!counts[k])counts[k]={display:w,count:0};counts[k].count++;});
    const sorted=Object.values(counts).sort((a,b)=>b.count-a.count);
    const maxCount=sorted[0]?.count||1;
    const mode=s.nuageMode||"taille";
    return(
      <div>
        <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),marginBottom:12}}>{vs.length} participant(s) · {allWords.length} mot(s) collectes</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:10,alignItems:"center",padding:"16px",background:"var(--card-inner)",borderRadius:"var(--radius-md)",minHeight:80}}>
          {sorted.map(({display,count})=>{
            const ratio=count/maxCount;
            const fontSize=mode==="taille"||mode==="les_deux"?Math.round(12+ratio*24):14;
            const weight=mode==="taille"||mode==="les_deux"?(ratio>0.6?900:ratio>0.3?700:500):500;
            return(
              <span key={display} style={{fontSize:taillePx(fontSize),fontWeight:weight,color:navTexte(C.white),lineHeight:1.2,position:"relative"}}>
                {display}
                {(mode==="compteur"||mode==="les_deux")&&count>1&&<sup style={{fontSize:"var(--fs-micro)",color:"var(--tx-orange)",fontWeight:700,marginLeft:1}}>{count}</sup>}
              </span>
            );
          })}
        </div>
        {!s.resultsPublished&&isAdmin&&<div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),marginTop:8}}>Vue admin · non publiee aux participants</div>}
      </div>
    );
  }

  // -- NOTATION --
  if(s.type==="notation"){
    const notes=vs.filter(v=>v.note!==undefined&&!isNaN(v.note)).map(v=>v.note);
    const max=s.notationType==="5"?5:10;
    const moy=notes.length>0?(notes.reduce((a,b)=>a+b,0)/notes.length):0;
    const dist={};for(let i=1;i<=max;i++)dist[i]=0;
    notes.forEach(n=>{if(dist[n]!==undefined)dist[n]++;});
    const comments=vs.filter(v=>v.commentaire&&v.commentaire.trim());
    return(
      <div>
        <div style={{display:"flex",gap:16,marginBottom:16,flexWrap:"wrap"}}>
          <div style={{background:"var(--card-inner)",borderRadius:"var(--radius-md)",padding:"16px 24px",textAlign:"center",flex:"0 0 auto"}}>
            <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),fontWeight:700,marginBottom:4}}>Moyenne</div>
            <div style={{fontSize:"var(--fs-plaque)",fontWeight:700,color:"var(--tx-orange)"}}>{moy.toFixed(1)}</div>
            <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray)}}>/ {max} · {notes.length} vote(s)</div>
          </div>
          <div style={{flex:"1 1 180px",minWidth:0}}>
            {Object.entries(dist).sort((a,b)=>parseInt(b[0])-parseInt(a[0])).map(([n,c])=>(
              <div key={n} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <div style={{minWidth:20,fontSize:"var(--fs-micro)",fontWeight:700,textAlign:"right",color:navTexte(parseInt(n)<=Math.round(moy)?C.red:C.green)}}>
                  {s.notationType==="5"?"★".repeat(parseInt(n)):n}
                </div>
                <Jauge valeur={notes.length>0?Math.round(c/notes.length*100):0} couleur={parseInt(n)<=Math.round(moy)?C.red:C.green} piste="var(--bg-card)" hauteur={8} rayon={4} style={{ flex:1 }} />
                <div style={{minWidth:16,fontSize:"var(--fs-micro)",color:navTexte(C.gray)}}>{c}</div>
              </div>
            ))}
          </div>
        </div>
        {comments.length>0&&<>
          <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),fontWeight:700,marginBottom:8}}>Commentaires ({comments.length})</div>
          <div style={{maxHeight:180,overflowY:"auto"}}>
            {comments.map((v,i)=>(
              <div key={v.id||i} style={{background:"var(--card-inner)",borderRadius:"var(--radius-sm)",padding:"8px 12px",marginBottom:6}}>
                {!s.secret&&<div style={{fontSize:"var(--fs-micro)",color:"var(--tx-teal)",fontWeight:700,marginBottom:2}}>{v.prenom} {v.nom} · note {v.note}/{max}</div>}
                <div style={{fontSize:"var(--fs-micro)",fontStyle:"italic"}}>{v.commentaire}</div>
              </div>
            ))}
          </div>
        </>}
      </div>
    );
  }

  // -- SCRUTIN DE LISTE --
  if(s.type==="liste"){
    const cands=s.candidates||[];
    const scores={};cands.forEach(c=>{scores[c]=0;});
    vs.forEach(v=>{(v.selectionnes||[]).forEach(c=>{if(scores[c]!==undefined)scores[c]++;});});
    const sorted=Object.entries(scores).sort((a,b)=>b[1]-a[1]);
    return(
      <div>
        <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),marginBottom:12}}>{vs.length} bulletins · {cands.length} candidat(s)</div>
        {sorted.map(([name,count],i)=>(
          <div key={name} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
            <div style={{width:22,height:22,borderRadius:"50%",background:i===0?C.orange:"var(--bg-card-dark)",display:"grid",placeItems:"center",fontSize:"calc(10px * var(--fs-texte, 1))",fontWeight:700,flexShrink:0}}>{i+1}</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:"var(--fs-micro)",marginBottom:3}}>{name}</div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <Jauge valeur={vs.length>0?Math.round(count/vs.length*100):0} couleur={i===0?C.orange:C.teal} piste="var(--bg-card)" hauteur={8} rayon={4} style={{ flex:1 }} />
                <div style={{fontSize:"var(--fs-micro)",fontWeight:700,minWidth:40}}>{count} / {vs.length}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // -- SWOT --
  if(s.type==="swot"){
    const quadrants=[{k:"forces",l:"Forces",c:C.green},{k:"faiblesses",l:"Faiblesses",c:C.red},{k:"opportunites",l:"Opportunites",c:C.teal},{k:"risques",l:"Risques",c:C.orange}];
    const publie=s.resultsPublished||isAdmin;
    const allContribs=vs.filter(v=>v.quadrant&&v.texte);

    const buildQuadrant=(key)=>{
      const items=allContribs.filter(v=>v.quadrant===key);
      const seen={};const merged=[];
      items.forEach(v=>{
        const k=normalizeWordGlobal(v.texte);
        if(!seen[k]){seen[k]={id:v.id,texte:v.texte,quadrant:v.quadrant,count:1,ids:[v.id]};merged.push(seen[k]);}
        else{seen[k].count++;seen[k].ids.push(v.id);}
      });
      return merged.sort((a,b)=>b.count-a.count);
    };

    if(!publie&&!isAdmin) return(
      <div className="results-hidden">
        <div style={{fontSize:"var(--fs-plaque)",marginBottom:8}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
        <div style={{fontWeight:700,fontSize:"var(--fs-meta)",color:navTexte(C.orangeL),marginBottom:5}}>SWOT non publie</div>
      </div>
    );

    return(
      <div>
        <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),marginBottom:12}}>{allContribs.length} contribution(s) · {[...new Set(allContribs.map(v=>v.participantId))].length} participant(s)</div>
        <div className="grid2" style={{gap:12}}>
          {quadrants.map(({k,l,c})=>{
            const items=buildQuadrant(k);
            return(
              <div key={k} style={{background:"var(--card-inner)",borderRadius:"var(--radius-md)",padding:"14px",border:"2px solid "+c,minHeight:100}}>
                <div style={{fontWeight:700,fontSize:"var(--fs-micro)",color:navTexte(c),textTransform:"uppercase",letterSpacing:"calc(.08em * var(--em-ajuste, 1))",marginBottom:10,display:"flex",justifyContent:"space-between"}}>
                  <span>{l}</span><span style={{fontWeight:400,fontSize:"var(--fs-micro)"}}>{items.length} mot(s)</span>
                </div>
                {items.map((item,i)=>(
                  <div key={item.id||i} style={{background:"var(--bg-card)",borderRadius:"var(--radius-sm)",padding:"8px 10px",marginBottom:6}}>
                    {isAdmin&&swotEdit?.id===item.id?(
                      // Mode edition
                      <div>
                        <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),marginBottom:4}}>Modifier :</div>
                        <input value={swotEdit.texte} onChange={e=>setSwotEdit(x=>({...x,texte:e.target.value}))}
                          className="inp" style={{marginBottom:6,fontSize:"max(var(--fs-micro), var(--fs-champ-min))"}}/>
                        <div style={{display:"flex",gap:6}}>
                          <button className="btn btn-green btn-sm" onClick={()=>{
                            const updated=(votesByScrutin[s.id]||[]).map(v=>item.ids.includes(v.id)?{...v,texte:swotEdit.texte}:v);
                            if(onSave&&appState)onSave({...appState,votesByScrutin:{...votesByScrutin,[s.id]:updated}});
                            setSwotEdit(null);
                          }}>Valider</button>
                          <button className="btn btn-ghost btn-sm" onClick={()=>setSwotEdit(null)}>Annuler</button>
                        </div>
                      </div>
                    ):(
                      // Mode affichage
                      <div>
                        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
                          <div style={{fontSize:"var(--fs-meta)",fontWeight:item.count>1?700:400,flex:1}}>
                            {item.texte}
                            {item.count>1&&<sup style={{fontSize:"var(--fs-micro)",color:navTexte(c),marginLeft:4,fontWeight:700}}>{item.count}</sup>}
                          </div>
                          {isAdmin&&<div style={{display:"flex",gap:4,flexShrink:0}}>
                            <button title="Modifier" onClick={()=>setSwotEdit({id:item.id,ids:item.ids,quadrant:k,texte:item.texte})}
                              style={{background:"none",border:"1px solid "+C.gray,borderRadius:"var(--radius-xs)",color:navTexte(C.gray),cursor:"pointer",fontSize:"var(--fs-micro)",padding:"2px 6px"}}>✎</button>
                            <button title="Supprimer" onClick={()=>{
                              const norm=normalizeWordGlobal(item.texte);
                              const updated=(votesByScrutin[s.id]||[]).filter(v=>!(v.quadrant===k&&normalizeWordGlobal(v.texte)===norm));
                              if(onSave&&appState)onSave({...appState,votesByScrutin:{...votesByScrutin,[s.id]:updated}});
                            }} style={{background:"none",border:"1px solid "+C.red,borderRadius:"var(--radius-xs)",color:"var(--tx-red)",cursor:"pointer",fontSize:"var(--fs-micro)",padding:"2px 6px"}}>✕</button>
                          </div>}
                        </div>
                        {isAdmin&&(
                          <div style={{marginTop:6,display:"flex",gap:4,flexWrap:"wrap"}}>
                            <span style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),alignSelf:"center"}}>Deplacer vers →</span>
                            {quadrants.filter(q=>q.k!==k).map(q=>(
                              <button key={q.k} title={"Deplacer vers "+q.l} onClick={()=>{
                                const norm=normalizeWordGlobal(item.texte);
                                const updated=(votesByScrutin[s.id]||[]).map(v=>
                                  v.quadrant===k&&normalizeWordGlobal(v.texte)===norm?{...v,quadrant:q.k}:v
                                );
                                if(onSave&&appState)onSave({...appState,votesByScrutin:{...votesByScrutin,[s.id]:updated}});
                              }} style={{background:"none",border:"1px solid "+q.c,borderRadius:"var(--radius-xs)",color:navTexte(q.c),cursor:"pointer",fontSize:"var(--fs-micro)",padding:"2px 8px",fontFamily:"Montserrat,sans-serif",fontWeight:700}}>
                                {q.l}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {items.length===0&&<div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),fontStyle:"italic",textAlign:"center",padding:"12px 0"}}>Aucune contribution</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const total=vs.length;
  const oui=vs.filter(v=>v.choice==="oui").length;
  const non=vs.filter(v=>v.choice==="non").length;
  const abst=vs.filter(v=>v.choice==="abstention").length;
  const manual=vs.filter(v=>v.manual);
  // Blancs et nuls d'un bulletin secret dépouillé : jamais des candidats.
  const {candidats:nomSorted,blancs:nbBlancs,nuls:nbNuls}=compterCandidats(vs);
  const bulletinSecret=modaliteVote(s)==="bulletin";
  return(
    <div>
      {/* Vote enregistré EN SÉANCE (à main levée) : le préciser, et donner le
          libellé solennel quand la saisie même dit « à l'unanimité ». */}
      {s.modeVote==="seance"&&vs.some(v=>v.seance)&&bulletinSecret&&(
        <div style={{display:"inline-flex",alignItems:"center",gap:6,marginBottom:10,padding:"4px 10px",borderRadius:"var(--radius-lg)",fontSize:"var(--fs-micro)",fontWeight:700,background:"rgba(149,27,129,.10)",color:"var(--tx-violet)"}}>
          <IconeTexte nom="checkbox"/> Vote à bulletin secret en séance : dépouillé en séance
        </div>
      )}
      {s.modeVote==="seance"&&vs.some(v=>v.seance)&&!bulletinSecret&&(
        <div style={{
          display:"inline-flex",alignItems:"center",gap:6,marginBottom:10,
          padding:"4px 10px",borderRadius:"var(--radius-lg)",fontSize:"var(--fs-micro)",fontWeight:700,
          background:s.seanceSaisie?.mode==="unanimite"
            ? (s.seanceSaisie.sens==="rejete"?"rgba(226,8,22,.12)":"rgba(148,193,31,.15)")
            : "rgba(244,152,0,.14)",
          color:s.seanceSaisie?.mode==="unanimite"
            ? (s.seanceSaisie.sens==="rejete"?"var(--tx-red)":"var(--tx-green, #4d7c0f)")
            : "var(--tx-orange)",
        }}>
          <IconeTexte nom="hand-stop"/> Vote en séance
          {s.seanceSaisie?.mode==="unanimite"&&
            ` – ${s.seanceSaisie.sens==="rejete"?"rejetée":"adoptée"} à l'unanimité`}
        </div>
      )}
      <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:10,marginBottom:12}}>
        <div style={{flex:1,minWidth:0}}>
          {s.type==="yon"?(
            <div style={{display:"flex",flexDirection:"column",gap:9}}>
              {[{l:"OUI",v:oui,c:C.green},{l:"NON",v:non,c:C.red},{l:"ABSTENTION",v:abst,c:C.gray}].map(({l,v,c})=>(
                <div key={l}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4,fontSize:"var(--fs-meta)",fontWeight:700}}>
                    <span style={{color:navTexte(c)}}>{l}</span><span>{v}{total>0?" — "+Math.round(v/total*100)+"%":""}</span>
                  </div>
                  <Prog value={v} max={total||1} color={c}/>
                </div>
              ))}
              {(nbBlancs>0||nbNuls>0)&&(
                <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray)}}>
                  {nbBlancs>0&&<span>Bulletins blancs : <b>{nbBlancs}</b></span>}
                  {nbBlancs>0&&nbNuls>0&&" · "}
                  {nbNuls>0&&<span>Bulletins nuls : <b>{nbNuls}</b></span>}
                  <span> — votants, non exprimés</span>
                </div>
              )}
            </div>
          ):(
            <div>
              {nomSorted.map(([name,count],i)=>(
                <div key={name} style={{display:"flex",alignItems:"center",gap:10,marginBottom:9}}>
                  <div style={{width:22,height:22,borderRadius:"50%",background:i===0?C.orange:"var(--bg-card-dark)",display:"grid",placeItems:"center",fontSize:"calc(10px * var(--fs-texte, 1))",fontWeight:700,flexShrink:0}}>{i+1}</div>
                  <div style={{flex:1}}><div style={{fontWeight:700,marginBottom:3,fontSize:"var(--fs-micro)"}}>{name}</div><Prog value={count} max={total||1} color={C.orange}/></div>
                  <div style={{fontWeight:700,fontSize:"var(--fs-corps)",minWidth:20}}>{count}</div>
                </div>
              ))}
              {(nbBlancs>0||nbNuls>0)&&(
                <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),marginTop:4}}>
                  {nbBlancs>0&&<span>Bulletins blancs : <b>{nbBlancs}</b></span>}
                  {nbBlancs>0&&nbNuls>0&&" · "}
                  {nbNuls>0&&<span>Bulletins nuls : <b>{nbNuls}</b></span>}
                </div>
              )}
              {manual.filter(v=>v.expression).map((v,i)=>(
                <div key={v.id||i} style={{background:"var(--card-inner)",borderRadius:"var(--radius-sm)",padding:"8px 12px",marginTop:6,fontStyle:"italic",color:navTexte(C.orangeL),fontSize:"var(--fs-micro)"}}>
                  <span className="tag-manual" style={{marginRight:8,fontStyle:"normal"}}>Manuel</span>{v.expression}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{textAlign:"right",flexShrink:0,marginLeft:16}}>
          <div style={{fontSize:"var(--fs-compteur)",fontWeight:700,color:"var(--tx-orange)"}}>{total}</div>
          <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray)}}>votes / {quorum} actifs</div>
          {manual.length>0&&<div style={{fontSize:"var(--fs-micro)",color:"var(--tx-violet)",marginTop:2}}>{manual.length} manuel(s)</div>}
        </div>
      </div>
      <div style={{marginBottom:12}}>
        <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),fontWeight:700,marginBottom:5}}>Participation</div>
        <Prog value={total} max={quorum||1} color={C.teal}/>
      </div>
      {/* Les bulletins dépouillés n'ont pas d'auteur : rien à détailler. */}
      {!s.secret&&vs.some(v=>!v.bulletin)&&(
        <>
          <hr className="sep"/>
          <div style={{fontSize:"var(--fs-micro)",color:"var(--tx-teal)",fontWeight:700,marginBottom:8}}>Détail public</div>
          <div className="scrollbox">
            {vs.filter(v=>!v.bulletin).map((v,i)=>(
              <div key={v.id||i} className="vrow">
                {v.expression?<span style={{fontStyle:"italic",color:navTexte(C.orangeL),flex:1}}>{v.expression}</span>
                  :<><span style={{fontWeight:600}}>{v.prenom} {v.nom}</span>
                    {v.pourNom&&v.pourNom!==v.nom&&<span className="tag-pouvoir">pouvoir de {v.pourPrenom} {v.pourNom}</span>}
                    <span style={{color:navTexte(C.gray),fontSize:"var(--fs-micro)"}}>{v.mutuelle}</span>
                    <span className={"chip chip-"+(v.choice==="oui"?"oui":v.choice==="non"?"non":v.choice==="abstention"?"abs":"nom")}>{v.choice}</span>
                  </>
                }
                {v.manual&&<span className="tag-manual">Manuel</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// `gestesAuBandeau` (grammaire du bandeau de commande, 27/10/2026, pages de la
// Gouvernance) : « Déposer un document » et « Nouveau dossier » sont la
// principale et la secondaire du BANDEAU de la page, qui les appelle par
// `apiRef.current.deposer()` / `.nouveauDossier()` ; la barre de la carte ne
// garde que le retour au dossier parent et « Actualiser » en bouton d'icône.
// Sans lui (Activité, Projets, pôles), la carte est celle d'avant.
export const DocumentLibrary = ({config, subPath, aramisSession, title="Documents", compact=false, canWrite=true, projects=[], lists=[], gestesAuBandeau=false, apiRef=null}) => {
  // La config SharePoint (siteUrl + rootFolder) est lue côté Firestore par la
  // Cloud Function graphProxy. Ici on a juste besoin de savoir si la config
  // de base existe pour afficher l'UI. Les credentials Azure sont côté backend.
  const confirm = useConfirm();
  const sp = config?.sharepoint || null;
  const isConfigured = !!(sp?.siteUrl);

  // ── State
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [items,     setItems]     = useState([]);
  const [uploading, setUploading] = useState(null); // {name, pct}
  const [newFolder, setNewFolder] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [apercuDoc, setApercuDoc] = useState(null); // pièce affichée dans la visionneuse
  const [videoDoc, setVideoDoc] = useState(null); // vidéo lue dans le lecteur
  const [currentSub, setCurrentSub] = useState(subPath || "");
  const fichierRef = useRef(null);
  const dossierRef = useRef(null);

  // Charge la liste du dossier courant
  const refreshList = async () => {
    if (!isConfigured || !aramisSession?.id) return;
    setLoading(true); setError("");
    try {
      const mod = await import("./sharepoint");
      const { items: list } = await mod.listFolder(aramisSession, currentSub);
      // tri : dossiers d'abord, puis fichiers, alphabétique
      const sorted = [...list].sort((a,b) => {
        const af = a.folder ? 0 : 1;
        const bf = b.folder ? 0 : 1;
        if (af !== bf) return af - bf;
        return (a.name||"").localeCompare(b.name||"", "fr");
      });
      setItems(sorted);
    } catch (e) {
      setError(e.message);
      setItems([]);
    }
    setLoading(false);
  };

  // Le dossier racine SUIT la prop : depuis la refonte Gouvernance, React
  // réutilise le même composant quand on navigue d'une instance à l'autre
  // (CA→AG) — currentSub restait figé sur le dossier initial et l'AG
  // affichait les documents du CA (bug signalé 26/08).
  useEffect(() => { setCurrentSub(subPath || ""); }, [subPath]);

  // Init — charge la liste dès qu'on a une session valide et la config
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshList(); }, [isConfigured, currentSub, aramisSession?.id]);

  // Actions — upload multi-fichiers séquentiel avec progress
  const [dragOver, setDragOver] = useState(false);

  const handleUploadMulti = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f && f.size > 0);
    if (files.length === 0) return;
    setError("");
    const mod = await import("./sharepoint");
    let errors = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploading({ name: file.name, pct: 0, current: i + 1, total: files.length });
      try {
        await mod.uploadFile(aramisSession, currentSub, file, (pct) => {
          setUploading({ name: file.name, pct, current: i + 1, total: files.length });
        });
      } catch (e) {
        errors.push(`${file.name}: ${e.message}`);
      }
    }
    setUploading(null);
    await refreshList();
    if (errors.length > 0) setError(errors.join("\n"));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!canWrite) return;
    const files = e.dataTransfer?.files;
    if (files?.length) handleUploadMulti(files);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  const handleDownload = async (item) => {
    setError("");
    try {
      const mod = await import("./sharepoint");
      const url = await mod.getDownloadUrl(aramisSession, item.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.name;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) { setError(e.message); }
  };

  // Suppression intelligente : recherche les copies du fichier partout
  const handleDelete = async (item) => {
    setError("");
    setDeleteLoading(true);
    try {
      const mod = await import("./sharepoint");
      // Rechercher les copies ET les raccourcis .url du fichier
      const result = await mod.searchByName(aramisSession, item.name);
      let allRelated = (result.items || []).filter(c => !c.folder);
      // Si c'est un fichier original (pas un .url), chercher aussi les raccourcis
      if (!isShortcut(item)) {
        try {
          const shortcutResult = await mod.searchByName(aramisSession, item.name + ".url");
          const shortcuts = (shortcutResult.items || []).filter(c => !c.folder && c.name === item.name + ".url");
          allRelated = [...allRelated, ...shortcuts];
        } catch(e) { /* ignore */ }
      }
      // Dédupliquer par id
      const seen = new Set();
      allRelated = allRelated.filter(c => { if(seen.has(c.id)) return false; seen.add(c.id); return true; });

      if (allRelated.length <= 1) {
        if (!(await confirm({title:"Supprimer le document",message:"Supprimer « "+item.name+" » ? Cette action est irréversible.",confirmLabel:"Supprimer",danger:true}))) {
          setDeleteLoading(false); return;
        }
        await mod.deleteItem(aramisSession, item.id);
        await refreshList();
      } else {
        // Plusieurs copies/raccourcis — ouvrir le modal de sélection
        setDeleteModal({
          item,
          copies: allRelated,
          selected: new Set([item.id]),
        });
      }
    } catch(e) {
      if (await confirm({title:"Supprimer le document",message:"Supprimer « "+item.name+" » ? Cette action est irréversible.",confirmLabel:"Supprimer",danger:true})) {
        try {
          const mod = await import("./sharepoint");
          await mod.deleteItem(aramisSession, item.id);
          await refreshList();
        } catch(e2) { setError(e2.message); }
      }
    }
    setDeleteLoading(false);
  };

  const executeMultiDelete = async () => {
    if (!deleteModal) return;
    const ids = [...deleteModal.selected];
    if (ids.length === 0) return;
    setDeleteLoading(true);
    setError("");
    try {
      const mod = await import("./sharepoint");
      await mod.deleteMultiple(aramisSession, ids);
      setDeleteModal(null);
      await refreshList();
    } catch(e) { setError(e.message); }
    setDeleteLoading(false);
  };

  const handleCreateFolder = async () => {
    if (!newFolder.trim()) return;
    setError("");
    try {
      const mod = await import("./sharepoint");
      await mod.ensureFolder(aramisSession, (currentSub?currentSub+"/":"") + newFolder.trim());
      setNewFolder("");
      setShowNewFolder(false);
      await refreshList();
    } catch (e) { setError(e.message); }
  };

  // Gestes du bandeau de la page (gestesAuBandeau) : déposer ouvre le choix
  // des fichiers (un clic de l'utilisateur, le navigateur l'accepte) ;
  // nouveau dossier ouvre la saisie du nom dans la carte.
  useEffect(() => {
    if (!apiRef) return undefined;
    apiRef.current = {
      deposer: () => { if (fichierRef.current) fichierRef.current.click(); },
      nouveauDossier: () => {
        setShowNewFolder(true);
        setTimeout(() => { if (dossierRef.current) { dossierRef.current.scrollIntoView({ block: "nearest" }); dossierRef.current.focus({ preventScroll: true }); } }, 0);
      },
    };
    return () => { apiRef.current = null; };
  });

  // Navigation : entrer dans un sous-dossier / remonter
  const enterFolder = (folderName) => {
    setCurrentSub(cs => (cs ? cs + "/" : "") + folderName);
  };
  const goUp = () => {
    setCurrentSub(cs => {
      const parts = (cs||"").split("/").filter(Boolean);
      if (parts.length === 0) return subPath || "";
      parts.pop();
      return parts.join("/");
    });
  };
  const atRoot = currentSub === (subPath || "");

  // ── Menu contextuel + Renommer + Affecter ──
  const [ctxMenu, setCtxMenu] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null); // {item, copies:[{id,path}], selected:Set}
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleRename = async () => {
    if (!renaming?.name?.trim()) return;
    setError("");
    try {
      const mod = await import("./sharepoint");
      await mod.renameItem(aramisSession, renaming.id, renaming.name.trim());
      setRenaming(null);
      await refreshList();
    } catch(e) { setError(e.message); }
  };

  const handleAssignTo = async (item, destSubPath) => {
    setAssignLoading(true);
    setError("");
    try {
      const mod = await import("./sharepoint");
      await mod.copyItemToAramis(aramisSession, item.id, destSubPath);
      setAssignModal(null);
      setAssignSearch("");
    } catch(e) { setError(e.message); }
    setAssignLoading(false);
  };

  // Destinations pour le modal Affecter à
  const assignDestinations = [
    ...(projects||[]).filter(p=>!p.clos).map(p=>({
      type:"projet", label:p.intitule||p.id, icon:"folder",
      subPath:(config?.sharepoint?.folders?.projets||"Projets Enio").trim()+"/"+((p.ref||p.id)+" — "+(p.intitule||"")).replace(/["*:<>?/\\|]/g,"").trim().slice(0,60).trim(),
    })),
    ...ORG_DOMAINES.flatMap(dom=>
      (dom.poles||[]).map(p=>({
        type:"pôle", label:p, icon:"users", group:dom.label,
        subPath:(config?.sharepoint?.folders?.poles||"Pôles")+"/"+(dom.folderName||dom.id)+"/"+p.replace(/["*:<>?/\\|]/g,"").trim().slice(0,60),
      }))
    ),
    {type:"instance",label:"Conseil d'Administration",icon:"clipboard-list",subPath:config?.sharepoint?.folders?.instance_ca||"Instances/Conseil d'Administration"},
    {type:"instance",label:"Assemblée Générale",icon:"users-group",subPath:config?.sharepoint?.folders?.instance_ag||"Instances/Assemblée Générale"},
    {type:"instance",label:"CODIR",icon:"building-community",subPath:config?.sharepoint?.folders?.instance_cd||"Instances/CODIR"},
    {type:"instance",label:"Bureau",icon:"briefcase",subPath:config?.sharepoint?.folders?.instance_bureau||"Instances/Bureau"},
    {type:"instance",label:"Documents communs",icon:"folder",subPath:config?.sharepoint?.folders?.instance_commun||"Instances/Documents communs"},
  ];

  // ── Rendu
  if (!isConfigured) {
    return (
      <div className="card" style={{marginBottom:14,
        borderLeft:"3px solid "+C.gold,background:"rgba(232,195,40,.04)"}}>
        <div style={{fontWeight:700,fontSize:"var(--fs-micro)",color:"var(--tx-gold)",marginBottom:6}}>
          <IconeTexte nom="folder"/> Documents SharePoint · non configuré
        </div>
        <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",lineHeight:1.5}}>
          L'intégration SharePoint n'est pas encore configurée.
          Un super-administrateur doit renseigner l'URL du site et le dossier
          racine dans <strong>Super-admin → SharePoint</strong>.
        </div>
      </div>
    );
  }

  if (!aramisSession?.id) {
    return (
      <div className="card" style={{marginBottom:14}}>
        <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",fontStyle:"italic",textAlign:"center",padding:20}}>
          Connecte-toi à Aramis pour accéder aux documents.
        </div>
      </div>
    );
  }

  const fmtSize = (b) => {
    if (!b && b !== 0) return "";
    if (b < 1024) return b + " o";
    if (b < 1024*1024) return Math.round(b/1024) + " Ko";
    if (b < 1024*1024*1024) return Math.round(b/1024/1024*10)/10 + " Mo";
    return Math.round(b/1024/1024/1024*10)/10 + " Go";
  };
  const isShortcut = (item) => !item.folder && (item.name||"").endsWith(".url");
  const shortcutDisplayName = (name) => name.replace(/\.url$/i, "");
  // Type du fichier : une icône du jeu commun (src/icones.js), plus d'emoji.
  const fileIcon = (item) => {
    if (item.folder) return "folder";
    if (isShortcut(item)) return "link";
    const ext = (item.name||"").split(".").pop()?.toLowerCase();
    if (["pdf"].includes(ext)) return "file-type-pdf";
    if (["doc","docx","odt","rtf"].includes(ext)) return "file-type-doc";
    if (["xls","xlsx","csv","ods"].includes(ext)) return "file-type-xls";
    if (["ppt","pptx","odp"].includes(ext)) return "file-type-ppt";
    if (["png","jpg","jpeg","gif","webp","svg","heic"].includes(ext)) return "photo";
    if (["zip","rar","7z"].includes(ext)) return "file-zip";
    if (["mp4","mov","m4v","avi","webm","mkv"].includes(ext)) return "video";
    if (["mp3","wav","flac","m4a"].includes(ext)) return "file-music";
    return "file-text";
  };
  // Ouvrir un raccourci .url : télécharger le contenu, parser l'URL, ouvrir
  const openShortcut = async (item) => {
    try {
      const mod = await import("./sharepoint");
      const url = await mod.getDownloadUrl(aramisSession, item.id);
      // Télécharger le contenu du .url pour extraire l'URL cible
      const resp = await fetch(url);
      const txt = await resp.text();
      const match = txt.match(/URL=(.+)/i);
      if (match && match[1]) {
        window.open(match[1].trim(), "_blank");
      } else {
        // Fallback : ouvrir le webUrl de l'item
        window.open(item.webUrl || url, "_blank");
      }
    } catch(e) { setError("Impossible d'ouvrir le raccourci : "+e.message); }
  };

  return (
    <div className="card" style={{marginBottom:14}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
        gap:10,marginBottom:10,flexWrap:"wrap"}}>
        <div style={{fontWeight:700,fontSize:"var(--fs-micro)",color:"var(--tx-teal)",
          fontFamily:"Montserrat,sans-serif"}}>
          {title}
          {currentSub && currentSub !== (subPath || "") && (
            <span style={{color:"var(--text-sub)",marginLeft:6,fontSize:"var(--fs-micro)",fontWeight:500}}>
              / {currentSub.replace((subPath||"")+"/","").replace(subPath||"","")}
            </span>
          )}
        </div>
        {gestesAuBandeau && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={refreshList} disabled={loading}
            aria-label={loading ? "Chargement…" : "Actualiser"} title={loading ? "Chargement…" : "Actualiser"}
            style={{minWidth:"var(--cible-min, 0px)",justifyContent:"center"}}>
            <IconeTexte nom="refresh"/>
          </button>
        )}
      </div>

      {/* Barre d'actions */}
      {gestesAuBandeau ? (
        <>
          {!atRoot && (
            <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={goUp}>
                <IconeTexte nom="arrow-left"/> Retour
              </button>
            </div>
          )}
          {canWrite && (
            <input ref={fichierRef} type="file" multiple style={{display:"none"}} aria-hidden="true" tabIndex={-1}
              onChange={e=>{handleUploadMulti(e.target.files); e.target.value="";}}/>
          )}
        </>
      ) : (
      <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
        {!atRoot && (
          <button className="btn btn-ghost btn-sm" onClick={goUp}>
            ← Retour
          </button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={refreshList} disabled={loading}>
          {loading?"⟳ Chargement…":"⟳ Actualiser"}
        </button>
        {canWrite && (
          <>
            <label className="btn btn-orange btn-sm" style={{cursor:"pointer"}}>
              <IconeTexte nom="arrow-up"/> Téléverser
              <input type="file" multiple style={{display:"none"}}
                onChange={e=>{handleUploadMulti(e.target.files); e.target.value="";}}/>
            </label>
            <button className="btn btn-ghost btn-sm"
              onClick={()=>setShowNewFolder(x=>!x)}>
              <IconeTexte nom="folder"/>+ Nouveau dossier
            </button>
          </>
        )}
      </div>
      )}

      {showNewFolder && (
        <div style={{display:"flex",gap:6,marginBottom:10,
          padding:10,background:"var(--card-inner,rgba(0,0,0,.03))",borderRadius:"var(--radius-sm)"}}>
          <input ref={dossierRef} className="inp" value={newFolder}
            onChange={e=>setNewFolder(e.target.value)}
            placeholder="Nom du dossier" aria-label="Nom du dossier"
            onKeyDown={e=>e.key==="Enter"&&handleCreateFolder()}/>
          <button className={gestesAuBandeau ? "btn btn-ghost btn-sm" : "btn btn-teal btn-sm"} onClick={handleCreateFolder}>Créer</button>
          <button className="btn btn-ghost btn-sm"
            onClick={()=>{setShowNewFolder(false);setNewFolder("");}}>Annuler</button>
        </div>
      )}

      {uploading && (
        <div style={{padding:10,background:"rgba(29, 148, 138,.08)",
          borderRadius:"var(--radius-sm)",marginBottom:10}}>
          <div style={{fontSize:"var(--fs-micro)",fontWeight:700,marginBottom:4,color:"var(--tx-teal)"}}>
            <IconeTexte nom="arrow-up"/> {uploading.current}/{uploading.total} – {uploading.name}
          </div>
          <Jauge valeur={uploading.pct} couleur={C.teal} piste="rgba(0,0,0,.08)" hauteur={6} rayon={3} />
          <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginTop:3}}>{uploading.pct}%</div>
        </div>
      )}

      {/* Zone de drag-and-drop */}
      {canWrite && !uploading && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={{border: dragOver ? "2px dashed #1D948A" : "2px dashed var(--border-color)",
            borderRadius:"var(--radius-md)", padding: dragOver ? 24 : 12, marginBottom:10,
            textAlign:"center", fontSize:"var(--fs-micro)", color: dragOver ? "var(--tx-teal)" : "var(--text-sub)",
            background: dragOver ? "rgba(29, 148, 138,.06)" : "transparent",
            transition:"background .2s, border-color .2s, color .2s, box-shadow .2s, opacity .2s, transform .2s", cursor:"default"}}>
          {dragOver
            ? <span style={{fontWeight:700}}>Déposer les fichiers ici</span>
            : <span><IconeTexte nom="paperclip"/> Glisser-déposer des fichiers ici pour les téléverser</span>
          }
        </div>
      )}

      {error && (
        <div style={{padding:10,borderRadius:"var(--radius-sm)",marginBottom:10,
          background:"rgba(226, 8, 22,.1)",border:"1px solid rgba(226, 8, 22,.3)",
          fontSize:"var(--fs-micro)",color:"#E20816",whiteSpace:"pre-wrap"}}>
          <IconeTexte nom="alert-triangle" couleur="var(--tx-orange)"/> {error}
        </div>
      )}

      {/* Liste */}
      {loading && items.length===0 ? (
        <SqueletteFichiers />
      ) : items.length === 0 ? (
        <div style={{textAlign:"center",padding:20,
          color:"var(--text-sub)",fontSize:"var(--fs-micro)",fontStyle:"italic"}}>
          Aucun document pour l'instant. {canWrite && "Téléverse un fichier ou crée un dossier."}
        </div>
      ) : (
        <div style={{position:"relative"}}>
          {items.map(it => {
            const shortcut = isShortcut(it);
            return (
            <div key={it.id}
              onClick={()=>{
                if(it.folder && !renaming) enterFolder(it.name);
                else if(shortcut && !renaming) openShortcut(it);
                else if(!it.folder && !renaming && estVideo(it.name)) setVideoDoc(it);
                else if(!it.folder && !renaming && AFFICHABLE_RX.test(it.name||"")) setApercuDoc(it);
              }}
              style={{display:"flex",alignItems:"center",gap:10,
                padding:"var(--filet-ecart) 10px",borderBottom:"1px solid var(--border-color)",
                cursor:(it.folder||shortcut||AFFICHABLE_RX.test(it.name||"")||estVideo(it.name))&&!renaming?"pointer":"default",
                transition:"background .12s",
                opacity:shortcut?0.85:1}}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(29, 148, 138,.05)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <span style={{flexShrink:0,display:"inline-flex",color:it.folder?"var(--tx-gold)":"var(--tx-teal)"}}><Icone name={fileIcon(it)} size={20}/></span>
              <div style={{flex:1,minWidth:0}}>
                {renaming?.id===it.id ? (
                  <div style={{display:"flex",gap:4}}>
                    <input className="inp" autoFocus value={renaming.name}
                      onChange={e=>setRenaming(r=>({...r,name:e.target.value}))}
                      onKeyDown={e=>{if(e.key==="Enter") handleRename(); if(e.key==="Escape") setRenaming(null);}}
                      onClick={e=>e.stopPropagation()}
                      style={{fontSize:"var(--fs-input-dense,11px)",padding:"2px 6px",flex:1}}/>
                    <button className="btn btn-teal btn-sm" style={{fontSize:"var(--fs-micro)"}} onClick={e=>{e.stopPropagation();handleRename();}}>✓</button>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:"var(--fs-micro)"}} onClick={e=>{e.stopPropagation();setRenaming(null);}}>✕</button>
                  </div>
                ) : (
                  <>
                    <div style={{fontSize:"var(--fs-micro)",fontWeight:600,color:"var(--text-main)",
                      whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",
                      display:"flex",alignItems:"center",gap:6}}>
                      {shortcut ? shortcutDisplayName(it.name) : it.name}
                      {shortcut && <span style={{fontSize:"var(--fs-micro)",fontWeight:700,padding:"1px 6px",
                        borderRadius:"var(--radius-sm)",background:"rgba(29, 148, 138,.12)",color:"var(--tx-teal)",
                        flexShrink:0}}>Raccourci</span>}
                    </div>
                    <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)"}}>
                      {it.folder ? ((it.folder.childCount||0)+" élément(s)") : shortcut ? "Lien vers l'original" : fmtSize(it.size)}
                      {it.lastModifiedDateTime && " · "+new Date(it.lastModifiedDateTime).toLocaleDateString("fr-FR")}
                    </div>
                  </>
                )}
              </div>
              <div style={{display:"flex",flexWrap:"wrap",justifyContent:"flex-end",gap:2,flexShrink:0,alignItems:"center"}}>
                {!it.folder && !shortcut && AFFICHABLE_RX.test(it.name||"") && (
                  <button onClick={e=>{e.stopPropagation();setApercuDoc(it);}} title="Afficher" aria-label={"Afficher "+it.name}
                    style={{background:"none",border:"none",cursor:"pointer",color:"var(--tx-teal)",fontSize:"var(--fs-nav)",padding:"2px 4px"}}><IconeTexte nom="eye" titre="Afficher"/></button>
                )}
                {!it.folder && !shortcut && estVideo(it.name) && (
                  <button onClick={e=>{e.stopPropagation();setVideoDoc(it);}} title="Lire" aria-label={"Lire "+it.name}
                    style={{background:"none",border:"none",cursor:"pointer",color:"var(--tx-teal)",fontSize:"var(--fs-nav)",padding:"2px 4px"}}><IconeTexte nom="player-play" titre="Lire"/></button>
                )}
                {!it.folder && (
                  <button onClick={e=>{e.stopPropagation();handleDownload(it);}} title="Télécharger"
                    style={{background:"none",border:"none",cursor:"pointer",color:"var(--tx-teal)",fontSize:"var(--fs-nav)",padding:"2px 4px"}}><IconeTexte nom="download" titre="Télécharger"/></button>
                )}
                {canWrite && (
                  <div>
                    {/* Position FIXED + ancrage calculé : sinon le menu est
                        clipé par overflow:hidden de la card projet parente
                        et les derniers items deviennent inaccessibles. */}
                    <button onClick={e=>{
                      e.stopPropagation();
                      if (ctxMenu?.item?.id===it.id) { setCtxMenu(null); return; }
                      const rect = e.currentTarget.getBoundingClientRect();
                      const MENU_H = 120; // estimation hauteur menu (3 items max)
                      const flipUp = rect.bottom + MENU_H > window.innerHeight - 8;
                      setCtxMenu({ item: it, anchor: {
                        right: Math.max(8, window.innerWidth - rect.right),
                        top: rect.bottom + 4,
                        bottom: window.innerHeight - rect.top + 4,
                        flipUp,
                      }});
                    }}
                      title="Actions" style={{background:"none",border:"none",cursor:"pointer",
                        color:"var(--text-sub)",fontSize:"var(--fs-corps)",padding:"2px 4px",fontWeight:700}}>⋯</button>
                    {ctxMenu?.item?.id===it.id && ctxMenu.anchor && (
                      <>
                        <div style={{position:"fixed",inset:0,zIndex:10005}} onClick={()=>setCtxMenu(null)}/>
                        <div style={{position:"fixed",
                          right: ctxMenu.anchor.right,
                          ...(ctxMenu.anchor.flipUp
                            ? { bottom: ctxMenu.anchor.bottom }
                            : { top: ctxMenu.anchor.top }),
                          zIndex:10006,
                          background:"var(--bg-card,#0a3446)",border:"1.5px solid var(--border-color)",
                          borderRadius:"var(--radius-sm)",padding:4,minWidth:160,boxShadow:"0 4px 16px rgba(0,0,0,.3)"}}>
                          <button onClick={e=>{e.stopPropagation();setRenaming({id:it.id,name:it.name});setCtxMenu(null);}}
                            style={{display:"block",width:"100%",textAlign:"left",padding:"6px 10px",fontSize:"var(--fs-micro)",
                              background:"none",border:"none",cursor:"pointer",color:"var(--text-main)",borderRadius:"var(--radius-xs)"}}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(29, 148, 138,.1)"}
                            onMouseLeave={e=>e.currentTarget.style.background="none"}>
                            <IconeTexte nom="notes"/> Renommer
                          </button>
                          {!it.folder && (
                            <button onClick={e=>{e.stopPropagation();setAssignModal(it);setCtxMenu(null);}}
                              style={{display:"block",width:"100%",textAlign:"left",padding:"6px 10px",fontSize:"var(--fs-micro)",
                                background:"none",border:"none",cursor:"pointer",color:"var(--text-main)",borderRadius:"var(--radius-xs)"}}
                              onMouseEnter={e=>e.currentTarget.style.background="rgba(29, 148, 138,.1)"}
                              onMouseLeave={e=>e.currentTarget.style.background="none"}>
                              <IconeTexte nom="clipboard-list"/> Affecter à...
                            </button>
                          )}
                          <button onClick={e=>{e.stopPropagation();handleDelete(it);setCtxMenu(null);}}
                            style={{display:"block",width:"100%",textAlign:"left",padding:"6px 10px",fontSize:"var(--fs-micro)",
                              background:"none",border:"none",cursor:"pointer",color:"var(--tx-red)",borderRadius:"var(--radius-xs)"}}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(226,8,22,.1)"}
                            onMouseLeave={e=>e.currentTarget.style.background="none"}>
                            <IconeTexte nom="trash"/> Supprimer
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );})
          }

          {/* Modal Affecter à... — règle 24/08 : le clic extérieur ouvre le
              dialogue de choix (ModalShell) ; la recherche saisie n'est plus
              jetée sur un clic accidentel. */}
          {assignModal && (
            <ModalShell onRequestClose={()=>{setAssignModal(null);setAssignSearch("");}}
              zIndex={10003} maxWidth={500} closable={!assignLoading}
              overlayStyle={{top:0,padding:16}}
              /* Hauteur d'origine (80vh) CONSERVEE, en dvh : iOS calcule vh
                 barres masquees, la carte depassait le visible. Au bureau
                 80dvh == 80vh, la fenetre est celle d'avant ; le second terme
                 la borne a la hauteur reellement offerte par le calque. */
              cardStyle={{borderRadius:"var(--radius-md)",padding:20,maxHeight:"min(var(--vh100) * 0.8, 100%)",overflowY:"auto",
                boxShadow:"0 8px 32px rgba(0,0,0,.2)"}}
              title={<><IconeTexte nom="clipboard-list"/> Affecter « {assignModal.name} » à...</>}>
                <input className="inp" value={assignSearch} autoFocus
                  onChange={e=>setAssignSearch(e.target.value)}
                  placeholder="Rechercher un projet, pôle ou instance..."
                  style={{marginBottom:10,fontSize:"var(--fs-input-dense,11px)"}}/>
                <div style={{maxHeight:350,overflowY:"auto"}}>
                  {["projet","pôle","instance"].map(type=>{
                    const filtered = assignDestinations.filter(d=>
                      d.type===type && (assignSearch.length<2 ||
                        (d.label+"|"+(d.group||"")).toLowerCase().includes(assignSearch.toLowerCase()))
                    );
                    if(filtered.length===0) return null;
                    return (
                      <div key={type} style={{marginBottom:10}}>
                        <div style={{fontSize:"var(--fs-micro)",fontWeight:700,color:"var(--tx-orange)",marginBottom:4,
                          fontFamily:"Montserrat,sans-serif"}}>
                          {type==="projet"?"Projets":type==="pôle"?"Pôles":"Instances"}
                        </div>
                        {filtered.slice(0,20).map((dest,i)=>(
                          <button key={i} onClick={()=>handleAssignTo(assignModal, dest.subPath)}
                            disabled={assignLoading}
                            style={{display:"block",width:"100%",textAlign:"left",padding:"6px 10px",
                              fontSize:"var(--fs-micro)",background:"none",border:"none",cursor:"pointer",
                              color:"var(--text-main)",borderRadius:"var(--radius-xs)",marginBottom:1}}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(29, 148, 138,.08)"}
                            onMouseLeave={e=>e.currentTarget.style.background="none"}>
                            <IconeTexte nom={dest.icon}/> {dest.label}
                            {dest.group && <span style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginLeft:6}}>{dest.group}</span>}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
                {assignLoading && <div style={{fontSize:"var(--fs-micro)",color:"var(--tx-teal)",marginTop:8}}>Copie en cours...</div>}
            </ModalShell>
          )}

          {/* Modal Suppression multi-emplacements — règle 24/08 (ModalShell) */}
          {deleteModal && (
            <ModalShell onRequestClose={()=>setDeleteModal(null)}
              zIndex={10003} maxWidth={500}
              overlayStyle={{top:0,padding:16}}
              cardStyle={{borderRadius:"var(--radius-md)",padding:20,
                boxShadow:"0 8px 32px rgba(0,0,0,.2)"}}
              title={<span style={{color:"var(--tx-red)"}}><IconeTexte nom="trash"/> Supprimer « {deleteModal.item.name} »</span>}>
                <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginBottom:12,lineHeight:1.5}}>
                  Ce fichier existe dans <strong>{deleteModal.copies.length} emplacement(s)</strong> (originaux et raccourcis).
                  Sélectionnez ceux où vous souhaitez le supprimer :
                </div>
                {deleteModal.copies.map(copy=>{
                  const checked = deleteModal.selected.has(copy.id);
                  const isSC = (copy.name||"").endsWith(".url");
                  const pathDisplay = (copy.path||copy.parentReference?.path||"")
                    .replace(/^\/drive\/root:\/?/, "")
                    .replace(/^Documents Aramis\/?/, "")
                    .replace(/\/[^/]*$/, "") || "(racine)";
                  return (
                    <label key={copy.id} style={{display:"flex",alignItems:"center",gap:8,
                      padding:"8px 10px",borderRadius:"var(--radius-sm)",marginBottom:4,cursor:"pointer",
                      background:checked?"rgba(226,8,22,.06)":"transparent",
                      border:checked?"1px solid rgba(226,8,22,.2)":"1px solid var(--border-color)"}}>
                      <input type="checkbox" checked={checked}
                        onChange={()=>setDeleteModal(prev=>{
                          const next = new Set(prev.selected);
                          if(next.has(copy.id)) next.delete(copy.id); else next.add(copy.id);
                          return {...prev, selected:next};
                        })}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:"var(--fs-micro)",fontWeight:600,color:"var(--text-main)",display:"flex",alignItems:"center",gap:6}}>
                          {pathDisplay}
                          {isSC&&<span style={{fontSize:"var(--fs-micro)",fontWeight:700,padding:"1px 6px",borderRadius:"var(--radius-sm)",
                            background:"rgba(29, 148, 138,.12)",color:"var(--tx-teal)"}}><IconeTexte nom="link"/> Raccourci</span>}
                          {!isSC&&copy.id===deleteModal.item.id&&<span style={{fontSize:"var(--fs-micro)",fontWeight:700,padding:"1px 6px",borderRadius:"var(--radius-sm)",
                            background:"rgba(244, 152, 0,.12)",color:"var(--tx-orange)"}}>Emplacement actuel</span>}
                        </div>
                        <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)"}}>
                          {copy.lastModified && new Date(copy.lastModified).toLocaleDateString("fr-FR")}
                        </div>
                      </div>
                    </label>
                  );
                })}
                <div style={{display:"flex",gap:8,justifyContent:"space-between",marginTop:14,alignItems:"center"}}>
                  <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)"}}>
                    {deleteModal.selected.size}/{deleteModal.copies.length} sélectionné(s)
                    {deleteModal.selected.size===deleteModal.copies.length &&
                      <span style={{color:"var(--tx-red)",fontWeight:700,marginLeft:6}}><IconeTexte nom="alert-triangle" couleur="var(--tx-orange)"/> Suppression totale</span>}
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setDeleteModal(null)}>Annuler</button>
                    <button className="btn btn-sm" disabled={deleteModal.selected.size===0||deleteLoading}
                      onClick={executeMultiDelete}
                      style={{background:"#E20816",color:"#fff",fontSize:"var(--fs-micro)"}}>
                      {deleteLoading ? "Suppression..." : `Supprimer (${deleteModal.selected.size})`}
                    </button>
                  </div>
                </div>
            </ModalShell>
          )}
        </div>
      )}
      {apercuDoc && (
        <Suspense fallback={null}>
          <ApercuDocumentRaw doc={apercuDoc} aramisSession={aramisSession}
            onClose={()=>setApercuDoc(null)} onDownload={handleDownload}/>
        </Suspense>
      )}
      {/* Vidéo : lue dans la page, source = l'URL pré-authentifiée que
          graphProxy remet après avoir relu le droit (assertAccesItem), comme
          au téléchargement ; un nouveau lien à chaque demande. */}
      {videoDoc && (
        <Suspense fallback={null}>
          <LecteurVideoDocumentRaw key={videoDoc.id} nom={videoDoc.name} taille={videoDoc.size}
            obtenirUrl={async ()=>{ const mod = await import("./sharepoint"); return mod.getDownloadUrl(aramisSession, videoDoc.id); }}
            onTelecharger={()=>handleDownload(videoDoc)} onClose={()=>setVideoDoc(null)}/>
        </Suspense>
      )}
    </div>
  );
};

/**
 * Les postes de gouvernance sont stockés par CODE (`president`, `sg`, `vp1`…).
 * Toute sortie lue par un humain — convocation .docx, signature du mail,
 * procès-verbal — doit passer par ici : sans cela le document affiche
 * « president » en bas de page (constaté à la recette du 08/09/2026 sur le
 * mail de convocation de l'AG).
 */
export const TITRES_GOUVERNANCE = {
  president: "Président", presidente: "Présidente",
  dg: "Directeur général", dga: "Directrice générale",
  vp1: "Premier Vice-président", vp2: "Deuxième Vice-président",
  vp3: "Troisième Vice-président", vp4: "Quatrième Vice-président",
  vp5: "Cinquième Vice-président",
  sg: "Secrétaire général", sga: "Secrétaire générale adjoint",
  tg: "Trésorier général", tga: "Trésorier général adjoint",
};
export const titreLisible = (t) =>
  TITRES_GOUVERNANCE[String(t || "").toLowerCase().trim()] || String(t || "");

/**
 * Observateur EXTÉRIEUR : invité depuis le CARNET DE CONTACTS EXTERNES
 * (`ajouterObservateurs`, Admin.js) — commissaire aux comptes, prestataire,
 * partenaire. Il porte `observateur: true`, n'a pas de fiche d'annuaire et
 * pas de compte Aramis. C'est le MÊME critère que la garde serveur
 * `peutVoirDocsSeance` : ne pas l'élargir sans la changer aussi.
 *
 * L'observateur INTERNE, lui, ne porte aucun drapeau : c'est un membre d'une
 * mutuelle sans mandat pour cette instance. Il a (ou peut créer) un compte
 * Aramis, et c'est un participant ordinaire du point de vue de la convocation.
 */
export const estObservateurExterieur = (p) => !!p && p.observateur === true;

/**
 * Qui reçoit le courriel de convocation — règle Pierre (08/09/2026, seconde
 * lecture) : « Je veux que les observateurs externes soient destinataires de
 * la convocation PDF, mais c'est tout. » Tout participant pourvu d'une
 * adresse est donc destinataire, observateurs compris, et tous reçoivent la
 * convocation officielle en pièce jointe.
 */
export const recoitConvocation = (p) => !!p && !!p.email;

/**
 * Qui accède aux PIÈCES de la séance (guichet documentaire Aramis) — miroir
 * exact de `peutVoirDocsSeance` (functions/sharepoint.js) :
 *   • l'observateur extérieur, jamais : « la convocation PDF, mais c'est tout » ;
 *   • tout autre participant selon le bouton 📁 / 🔒 de la liste des invités
 *     (`sansDocuments`) — convocation seule, ou convocation ET documents.
 */
export const peutVoirDocumentsSeance = (p) =>
  !!p && !p.sansDocuments && !estObservateurExterieur(p);
